import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from "react";
import Select from "react-select";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import ModalTableInput from "../../../ui/ModalTableInput";
import FileUploadSection from "../../../ui/FileUploadSection";
import MobileCapture from "../../../ui/MobileCapture";
import {
  swalError,
  swalSuccess,
  swalWarning,
  swalConfirm,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import { CONFIG } from "../../../../config/config";
import { saveAs } from "file-saver";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { AuthContext } from "../../../../context/AuthContext";
import QuestionDiagramResizeModal from "../../../ui/QuestionDiagramResizeModal";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;
const MAX_TOTAL_QUESTIONS = CONFIG.MAX_QUESTIONS_PER_QB;

const formatLatexText = (text) => {
  if (!text || typeof text !== "string") return text;
  let processed = text;
  if (!processed.includes("$") && !processed.includes("\\(") && !processed.includes("\\[")) {
    processed = processed.split(" ").map((word) => {
      if (/(\\[a-zA-Z]+|\^|_)/.test(word)) {
        return `$${word}$`;
      }
      return word;
    }).join(" ");
  }
  processed = processed.replace(/(^|[^\\])\$([^$]+)\$/g, (match, before, math) => {
    if (math.includes('\\displaystyle')) return match;
    return `${before}$\\displaystyle ${math}$`;
  });
  processed = processed.replace(/\\\((.*?)\\\)/g, (match, math) => {
    if (math.includes('\\displaystyle')) return match;
    return `\\(\\displaystyle ${math}\\)`;
  });
  return processed;
};

const LazyMarkdown = React.memo(({ children }) => (
  <ReactMarkdown
    children={children}
    remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
    rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
  />
));

const GenerateQuestionPaperModal = ({
  isOpen,
  onClose,
  selectedCount = 0,
  classDetails = null,
  topics = [],
  onGenerate,
  isGenerating = false,
}) => {
  const [format, setFormat] = useState("pdf");
  const [paperTitle, setPaperTitle] = useState("");
  const [exportSolution, setExportSolution] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const defaultTitle = classDetails?.name
        ? `${classDetails.name} - Question Paper`
        : "Question Paper";
      setPaperTitle(defaultTitle);
    }
  }, [isOpen, classDetails]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedCount === 0 || isGenerating) return;

    onGenerate({
      format,
      paperTitle: paperTitle.trim() || "Question Paper",
      exportSolution,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isGenerating && onClose()}
      maxWidth="560px"
      padding="0"
    >
      <div className="gqp-modal-container">
        {/* Modal Header */}
        <div className="gqp-modal-header">
          <div className="gqp-header-left">
            <div className="gqp-header-icon">
              <i className="fa-solid fa-file-lines"></i>
            </div>
            <div>
              <h2 className="gqp-modal-title">Generate Question Paper</h2>
              <p className="gqp-modal-subtitle">
                Export {selectedCount} question{selectedCount > 1 ? "s" : ""} from{" "}
                {classDetails?.name || "Question Bank"}
              </p>
            </div>
          </div>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="gqp-modal-body">
          {/* Document Title */}
          <div className="gqp-form-group">
            <label className="gqp-label" htmlFor="gqp-title-input">
              Document Title <span className="gqp-required">*</span>
            </label>
            <input
              id="gqp-title-input"
              type="text"
              className="gqp-input"
              value={paperTitle}
              onChange={(e) => setPaperTitle(e.target.value)}
              placeholder="e.g. Midterm Practice Paper"
              disabled={isGenerating}
              required
            />
          </div>

          {/* Format Selection */}
          <div className="gqp-form-group">
            <label className="gqp-label">Export Format</label>
            <div className="gqp-cards-grid">
              <div
                className={`gqp-select-card ${format === "pdf" ? "active" : ""}`}
                onClick={() => !isGenerating && setFormat("pdf")}
              >
                <div className="gqp-card-radio">
                  <div className="gqp-radio-dot"></div>
                </div>
                <div className="gqp-card-info">
                  <div className="gqp-card-title">
                    <i className="fa-solid fa-file-pdf gqp-icon-pdf"></i> PDF Document
                  </div>
                  <div className="gqp-card-desc">
                    Print-ready vector PDF layout
                  </div>
                </div>
              </div>

              <div
                className={`gqp-select-card ${format === "docx" ? "active" : ""}`}
                onClick={() => !isGenerating && setFormat("docx")}
              >
                <div className="gqp-card-radio">
                  <div className="gqp-radio-dot"></div>
                </div>
                <div className="gqp-card-info">
                  <div className="gqp-card-title">
                    <i className="fa-solid fa-file-word gqp-icon-word"></i> Word (.docx)
                  </div>
                  <div className="gqp-card-desc">
                    Editable Microsoft Word file
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Solutions Option */}
          <div className="gqp-solutions-box">
            <div className="gqp-solutions-text">
              <div className="gqp-solutions-title">
                <i className="fa-regular fa-lightbulb"></i>
                Include Step-by-Step Solutions
              </div>
              <div className="gqp-solutions-desc">
                {format === "pdf"
                  ? "Downloads an additional Solutions & Answer Key PDF alongside the Question Paper."
                  : "Includes full answers and solution steps in the document."}
              </div>
            </div>
            <label className="gqp-switch">
              <input
                type="checkbox"
                checked={exportSolution}
                onChange={(e) => setExportSolution(e.target.checked)}
                disabled={isGenerating}
              />
              <span className="gqp-slider"></span>
            </label>
          </div>

          {/* Modal Footer */}
          <div className="gqp-modal-footer">
            <button
              type="button"
              className="gqp-btn gqp-btn-secondary"
              onClick={onClose}
              disabled={isGenerating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="gqp-btn gqp-btn-primary"
              disabled={isGenerating || selectedCount === 0}
            >
              {isGenerating ? (
                <>
                  <Loader size="small" color="#ffffff" />
                </>
              ) : (
                <>
                  <i className="fa-solid fa-download"></i>
                  Generate {format.toUpperCase()}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

const ManageQuestions = ({ classId }) => {
  const { user } = useContext(AuthContext);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [isSolutionModalOpen, setIsSolutionModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [imageURL, setImageURL] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDiagramImageModalOpen, setIsDiagramImageModalOpen] = useState(false);
  const [diagramQuestion, setDiagramQuestion] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [isAddQuestionBankModalOpen, setIsAddQuestionBankModalOpen] =
    useState(false);
  const [newQuestionBank, setNewQuestionBank] = useState({
    name: "",
    customInstructions: "",
    source: {
      texts: [""],
      links: [""],
      executionMode: "GENERATE",
      files: [],
    },
    withSolution: false,
    entries: [
      {
        topic: "",
        subTopic: "",
        difficulty: [], // Changed to array for multi-select
        questionCount: 5,
        questionType: [], // Changed to array for multi-select
        mcqSubTypes: [],
      },
    ],
  });
  const [difficultyOptions, setDifficultyOptions] = useState([]);
  const [questionTypeOptions, setQuestionTypeOptions] = useState([]);
  const [mcqSubTypeOptions, setMcqSubTypeOptions] = useState([]);
  const [mcqSubTypePopupRowIndex, setMcqSubTypePopupRowIndex] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(""); // Add progress tracking
  const [progressPhase, setProgressPhase] = useState(""); // Add phase tracking for visual states
  const [regenerateImageQuestion, setRegenerateImageQuestion] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState({});
  const [isSolutionLoading, setIsSolutionLoading] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [isGenerateQPModalOpen, setIsGenerateQPModalOpen] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());

  const selectedTopics = useMemo(() => {
    if (!selectedQuestionIds || selectedQuestionIds.size === 0) return [];
    const set = new Set();
    questions.forEach((q) => {
      if (selectedQuestionIds.has(q._id) && q.topic) {
        set.add(q.topic);
      }
    });
    return Array.from(set);
  }, [questions, selectedQuestionIds]);
  const [classDetails, setClassDetails] = useState(null); // State to hold class details
  const hasFetched = useRef(false);
  const hasFetchedClassDetails = useRef(false); // Ref to track class details fetch
  const tableRef = useRef(null);
  const [ImageAllow, setImageAllow] = useState(false);

  // ── Image Options & Upload Modals state ──
  const [isImageOptionsModalOpen, setIsImageOptionsModalOpen] = useState(false);
  const [selectedImageQuestion, setSelectedImageQuestion] = useState(null);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [selectedUploadFile, setSelectedUploadFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // ── Edit / Delete state ──
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [editForm, setEditForm] = useState({
    questionText: "",
    options: [],
    topic: "",
    subTopic: "",
    type: "",
    difficulty: "",
  });
  const [initialEditForm, setInitialEditForm] = useState({
    questionText: "",
    options: [],
    topic: "",
    subTopic: "",
    type: "",
    difficulty: "",
    solution: "",
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [isQBTypeModalOpen, setIsQBTypeModalOpen] = useState(false);
  const [isExtractPdfOpen, setIsExtractPdfOpen] = useState(false);
  const [isExtractingPdf, setIsExtractingPdf] = useState(false);
  const [extractPdfProgress, setExtractPdfProgress] = useState("");
  const [extractPdfForm, setExtractPdfForm] = useState({
    questionBankName: "",
    numberOfQs: "",
    sourceTestPaper: null,
    answerKeysFile: null,
    generateSolutions: false,
    customInstructions: "",
  });
  const [extractedQuestions, setExtractedQuestions] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureFor, setCaptureFor] = useState(null);



  // Fetch class details
  const fetchClassDetails = useCallback(async () => {
    if (!classId) return;
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/class/get/${classId}`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch class details");
      }
      setClassDetails(data);
    } catch (err) {
      // Handle error appropriately, maybe set an error state or log
      swalError(err.message);
    }
  }, [classId]);

  // Effect to fetch class details
  useEffect(() => {
    if (hasFetchedClassDetails.current || !classId) return;
    fetchClassDetails();
    hasFetchedClassDetails.current = true;
  }, [fetchClassDetails, classId]);

  // Fetch institute details

  useEffect(() => {
    const fetchInstitute = async () => {
      if (!user?.instituteId) return;
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get/${user.instituteId}`,
        { credentials: "include" }
      );
      if (!response) return;
      const data = await response.json();
      setImageAllow(data.isImageCreationAllowed);
    };
    fetchInstitute();
  }, [user]);

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        swalSuccess("Copied to clipboard!");
      })
      .catch((err) => {
        swalError("Failed to copy text: " + err);
      });
  };

  const copyImageToClipboard = async (imageURL) => {
    try {
      const response = await fetch(imageURL);
      const blob = await response.blob();
      const item = new ClipboardItem({ "image/png": blob });
      await navigator.clipboard.write([item]);
      swalSuccess("Image copied to clipboard!");
    } catch (err) {
      swalError("Failed to copy image: " + err);
    }
  };

  const showSolution = (question) => {
    setActiveQuestion(question);
    setIsSolutionModalOpen(true);
  };
  const showImage = (question) => {
    if (!question) return;
    // diagramFileURL only → dedicated diagram viewer
    if (question.diagramFileURL && !question.imageURL && !question.svgCode) {
      setDiagramQuestion(question);
      setIsDiagramImageModalOpen(true);
      return;
    }
    // SVG / LaTeX image → existing viewer
    if (question.imageURL || question.svgCode) {
      setImageURL(question.imageURL || null);
      setRegenerateImageQuestion(question);
      setIsImageModalOpen(true);
    }
  };

  const handleDiagramSaveSuccess = useCallback((questionId, newDiagramFileURL, newBoundingBox) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) =>
        q._id === questionId
          ? { ...q, diagramFileURL: newDiagramFileURL, diagramBoundingBox: newBoundingBox }
          : q
      )
    );
    setExtractedQuestions((prevExtracted) =>
      prevExtracted.map((q) =>
        q._id === questionId || q.questionId === questionId || q._sourceId === questionId
          ? { ...q, diagramFileURL: newDiagramFileURL, diagramBoundingBox: newBoundingBox }
          : q
      )
    );
    setDiagramQuestion((prev) => {
      if (prev && (prev._id === questionId || prev.questionId === questionId)) {
        return { ...prev, diagramFileURL: newDiagramFileURL, diagramBoundingBox: newBoundingBox };
      }
      return prev;
    });
  }, []);

  const showQuestion = (question) => {
    setSelectedQuestion(question);
    setIsQuestionModalOpen(true);
  };

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/question-bank/get-all-questions/${classId}`,
        {
          credentials: "include",
        }
      );

      if (!response) return;

      const data = await response.json();
      const mappedData = data.map((q) => ({
        ...q,
        diagramSuggestedText: q.diagramSuggested ? "Yes" : "No",
      }));
      setQuestions(mappedData);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (hasFetched.current) return;
    fetchQuestions();
    hasFetched.current = true;
  }, [fetchQuestions]);

  const fetchQBDifficultyLOVs = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/QB-difficulty`,
        {
          credentials: "include",
        }
      );

      if (!response) return;

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch difficulty options");
      }

      // Add label to the options
      const optionsWithLabels = data.map((item) => {
        return { ...item, label: item.value };
      });
      setDifficultyOptions(optionsWithLabels);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    }
  };
  const fetchQuestionTypeLOVs = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/QB-questionType`,
        {
          credentials: "include",
        }
      );

      if (!response) return;

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Failed to fetch question type options"
        );
      }

      // Add label to the options
      const optionsWithLabels = data.map((item) => {
        return { ...item, label: item.value };
      });
      setQuestionTypeOptions(optionsWithLabels);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    }
  };
  const fetchMCQSubTypeLOVs = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/MCQSubTypes`,
        {
          credentials: "include",
        }
      );

      if (!response) return;

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Failed to fetch MCQ sub-type options"
        );
      }

      const optionsWithLabels = data.map((item) => {
        return { ...item, label: item.value };
      });
      setMcqSubTypeOptions(optionsWithLabels);
    } catch (err) {
      // Non-critical: if this LOV doesn't exist yet, just leave the array empty
      console.warn("MCQSubTypes LOV fetch:", err.message);
    }
  };

  useEffect(() => {
    fetchQBDifficultyLOVs();
    fetchQuestionTypeLOVs();
    fetchMCQSubTypeLOVs();
  }, []);

  const convertMcqForReview = useCallback((question, index) => {
    const optionsArr = (question.options || []).map((o) => ({
      key: o.key,
      text: o.text,
    }));
    const correctOpts = Array.isArray(question.correctOptions)
      ? question.correctOptions
      : [];
    return {
      _id: question._id,
      questionNumber: String(index + 1),
      questionText: question.questionText || "",
      options: optionsArr,
      correctOptions: correctOpts,
      _sourceId: question._id,
      questionId: question._id,
      _sourceSolution: question.solution || "",
      diagramFileURL: question.diagramFileURL || "",
      diagramPageFileURL: question.diagramPageFileURL || "",
      diagramBoundingBox: question.diagramBoundingBox || null,
      svgCode: question.svgCode || "",
    };
  }, []);

  const handleExtractFromPdf = useCallback(async () => {
    if (!extractPdfForm.questionBankName.trim()) {
      swalError("Question Bank name is required.");
      return;
    }
    if (!extractPdfForm.numberOfQs) {
      swalError("Please enter the number of questions you want to extract.");
      return;
    }
    const numQs = parseInt(extractPdfForm.numberOfQs);
    if (isNaN(numQs) || numQs <= 0) {
      swalError("Please enter a valid number of questions to extract.");
      return;
    }
    if (!extractPdfForm.sourceTestPaper) {
      swalError("Please upload the test paper.");
      return;
    }

    setIsExtractingPdf(true);
    setExtractPdfProgress("Uploading test paper...");

    try {
      const formData = new FormData();
      formData.append("name", extractPdfForm.questionBankName.trim());
      formData.append("sourceTestPaper", extractPdfForm.sourceTestPaper);
      formData.append("numberOfQs", extractPdfForm.numberOfQs);
      if (extractPdfForm.answerKeysFile) {
        formData.append("answerKeysFile", extractPdfForm.answerKeysFile);
      }
      if (extractPdfForm.customInstructions?.trim()) {
        formData.append("customInstructions", extractPdfForm.customInstructions.trim());
      }

      setExtractPdfProgress("Extracting questions from PDF... This may take a minute.");

      const response = await fetchWithAuth(
        `${serverUrl}/api/question-bank/extract-questions/${classId}`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );
      if (!response) {
        setIsExtractingPdf(false);
        return;
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to extract questions from PDF");
      }

      // Generate solutions if toggle is on
      if (extractPdfForm.generateSolutions && data.questionIds && data.questionIds.length > 0) {
        setExtractPdfProgress("Generating solutions...");
        try {
          const solutionResponse = await fetchWithAuth(
            `${serverUrl}/api/question-bank/generate-question-solution`,
            {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ questionIds: data.questionIds }),
            }
          );
          if (!solutionResponse || !solutionResponse.ok) {
            console.warn("Solution generation completed with warnings.");
          }
        } catch (solutionError) {
          console.error("Solution generation error:", solutionError);
        }
      }

      setExtractPdfProgress("Fetching extracted questions...");

      // Fetch the extracted questions to get full data
      const questionsResponse = await fetchWithAuth(
        `${serverUrl}/api/question-bank/get-all-questions/${classId}`,
        { credentials: "include" }
      );
      if (!questionsResponse) {
        setIsExtractingPdf(false);
        return;
      }
      const allQuestions = await questionsResponse.json();

      // Filter to only the newly extracted questions
      const extracted = allQuestions.filter(
        (q) => data.questionIds?.includes(q._id)
      );

      if (extracted.length === 0) {
        swalWarning(
          "No questions were extracted from the PDF. Please check the test paper format."
        );
        setIsExtractingPdf(false);
        setExtractPdfProgress("");
        return;
      }

      const convertedQuestions = extracted.map((q, i) =>
        convertMcqForReview(q, i)
      );

      setExtractedQuestions(convertedQuestions);
      swalSuccess(
        `${convertedQuestions.length} question(s) extracted successfully! Please review them.`
      );
    } catch (error) {
      console.error("Error extracting questions from PDF:", error);
      swalError(error.message || "Failed to extract questions from PDF.");
    } finally {
      setIsExtractingPdf(false);
      setExtractPdfProgress("");
    }
  }, [extractPdfForm, classId, convertMcqForReview]);

  const handleReviewQuestionChange = async (index, field, value) => {
    setExtractedQuestions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (field === "correctOptions") {
      const question = extractedQuestions[index];
      if (question && question._id) {
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/question-bank/update-question/${question._id}`,
            {
              method: "PATCH",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                correctOptions: value,
              }),
            }
          );
          if (!response || !response.ok) {
            console.error("Failed to sync correct options with backend");
          }
        } catch (err) {
          console.error("Error updating question correct options:", err);
        }
      }
    }
  };

  const handleReviewQuestionRemove = async (index) => {
    const question = extractedQuestions[index];
    if (!question) return;

    const confirmed = await swalConfirm(
      "Are you sure you want to delete this question? This will permanently delete it from the question bank."
    );
    if (!confirmed) return;

    try {
      if (question._id) {
        const response = await fetchWithAuth(
          `${serverUrl}/api/question-bank/delete-question/${question._id}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        if (response && response.ok) {
          setExtractedQuestions((prev) => prev.filter((_, i) => i !== index));
          swalSuccess("Question deleted successfully.");
        } else {
          swalError("Failed to delete question from backend.");
        }
      }
    } catch (err) {
      console.error("Error deleting question:", err);
      swalError("Error deleting question: " + err.message);
    }
  };

  const handleFinishReview = async () => {
    setIsExtractPdfOpen(false);
    setExtractedQuestions([]);
    const savedName = extractPdfForm.questionBankName.trim();
    setExtractPdfForm({
      questionBankName: "",
      numberOfQs: "",
      sourceTestPaper: null,
      answerKeysFile: null,
      generateSolutions: false,
      customInstructions: "",
    });

    await fetchQuestions();

    if (savedName) {
      setTimeout(() => {
        if (tableRef.current && tableRef.current.setFilter) {
          tableRef.current.setFilter("questionBankName", savedName);
        }
      }, 300);
    }

    swalSuccess("Question bank questions successfully reviewed and saved!");
  };

  const handleFromTestPaperClick = () => {
    setExtractPdfForm({
      questionBankName: "",
      numberOfQs: "",
      sourceTestPaper: null,
      answerKeysFile: null,
      generateSolutions: false,
      customInstructions: "",
    });
    setExtractedQuestions([]);
    setIsExtractPdfOpen(true);
  };

  const onCaptureComplete = (pdfFile) => {
    if (window.__fileUploadCallback) {
      window.__fileUploadCallback(pdfFile);
      window.__fileUploadCallback = null;
    }

    if (captureFor === "create-test-paper") {
      setExtractPdfForm((prev) => ({
        ...prev,
        sourceTestPaper: pdfFile,
      }));
    } else if (captureFor === "create-answer-key") {
      setExtractPdfForm((prev) => ({
        ...prev,
        answerKeysFile: pdfFile,
      }));
    } else if (captureFor === "upload-diagram") {
      setSelectedUploadFile(pdfFile);
    }
    setIsCapturing(false);
    setCaptureFor(null);
  };

  const handleCloseCapture = () => {
    setIsCapturing(false);
    setCaptureFor(null);
  };


  const clampQuestionCount = (value) => {
    // Handle empty string or non-numeric values
    if (value === "" || value === null || value === undefined) return 1;
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1) return 1;
    if (numValue > MAX_TOTAL_QUESTIONS) return MAX_TOTAL_QUESTIONS; // Changed from MAX_QUESTIONS_PER_QB
    return numValue;
  };

  const addNewEntry = () => {
    setNewQuestionBank((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          topic: "",
          subTopic: "",
          difficulty: [],
          questionCount: 5,
          questionType: [],
          mcqSubTypes: [],
        },
      ],
    }));
  };

  const removeEntry = (index) => {
    if (newQuestionBank.entries.length > 1) {
      setNewQuestionBank((prev) => ({
        ...prev,
        entries: prev.entries.filter((_, i) => i !== index),
      }));
    }
  };

  const updateEntry = (index, field, value) => {
    // Special validation for questionType field when dealing with Comprehension
    if (field === "questionType") {
      const comprehensionOption = value?.find(
        (option) => option.value === "Comprehension"
      );
      const otherOptions = value?.filter(
        (option) => option.value !== "Comprehension"
      );

      // If Comprehension is selected with other options
      if (comprehensionOption && otherOptions.length > 0) {
        swalWarning(
          "Comprehension questions must be created as a standalone question type and cannot be combined with other question types.",
          { timer: 5000, timerProgressBar: true }
        );
        return; // Don't update the state
      }

      // If trying to add Comprehension when other options already exist
      const currentEntry = newQuestionBank.entries[index];
      const currentOtherOptions =
        currentEntry.questionType?.filter(
          (option) => option.value !== "Comprehension"
        ) || [];
      const isAddingComprehension =
        comprehensionOption &&
        !currentEntry.questionType?.find(
          (option) => option.value === "Comprehension"
        );

      if (isAddingComprehension && currentOtherOptions.length > 0) {
        swalWarning(
          "Comprehension questions must be created as a standalone question type. Please remove other question types first.",
          { timer: 5000, timerProgressBar: true }
        );
        return; // Don't update the state
      }

      // If trying to add other options when Comprehension already exists
      const currentComprehension = currentEntry.questionType?.find(
        (option) => option.value === "Comprehension"
      );
      const isAddingOtherOptions =
        otherOptions.length > 0 &&
        !currentEntry.questionType?.some((option) =>
          otherOptions.find((newOpt) => newOpt.value === option.value)
        );

      if (currentComprehension && isAddingOtherOptions) {
        swalWarning(
          "Comprehension questions must be created as a standalone question type. Please remove Comprehension first to add other question types.",
          { timer: 5000, timerProgressBar: true }
        );
        return; // Don't update the state
      }
    }

    setNewQuestionBank((prev) => ({
      ...prev,
      entries: prev.entries.map((entry, i) => {
        if (i === index) {
          const updatedEntry = { ...entry, [field]: value };
          if (field === "questionType") {
            const hasMCQ = (value || []).some(
              (opt) => opt.value && opt.value.toUpperCase().includes("MCQ")
            );
            if (!hasMCQ) {
              updatedEntry.mcqSubTypes = [];
            }
          }
          return updatedEntry;
        }
        return entry;
      }),
    }));
  };

  const addSourceText = () => {
    setNewQuestionBank((prev) => ({
      ...prev,
      source: {
        ...prev.source,
        texts: [...(prev.source?.texts || []), ""],
      },
    }));
  };

  const removeSourceText = (index) => {
    setNewQuestionBank((prev) => ({
      ...prev,
      source: {
        ...prev.source,
        texts: (prev.source?.texts || []).filter((_, i) => i !== index),
      },
    }));
  };

  const updateSourceText = (index, value) => {
    setNewQuestionBank((prev) => ({
      ...prev,
      source: {
        ...prev.source,
        texts: (prev.source?.texts || []).map((item, i) =>
          i === index ? value : item
        ),
      },
    }));
  };

  const addSourceLink = () => {
    setNewQuestionBank((prev) => ({
      ...prev,
      source: {
        ...prev.source,
        links: [...(prev.source?.links || []), ""],
      },
    }));
  };

  const removeSourceLink = (index) => {
    setNewQuestionBank((prev) => ({
      ...prev,
      source: {
        ...prev.source,
        links: (prev.source?.links || []).filter((_, i) => i !== index),
      },
    }));
  };

  const updateSourceLink = (index, value) => {
    setNewQuestionBank((prev) => ({
      ...prev,
      source: {
        ...prev.source,
        links: (prev.source?.links || []).map((item, i) =>
          i === index ? value : item
        ),
      },
    }));
  };

  const handleAddQuestionBank = async (e) => {
    e.preventDefault();

    // Validate total question count
    const totalQuestions = calculateTotalQuestions(newQuestionBank.entries);
    if (totalQuestions > MAX_TOTAL_QUESTIONS) {
      swalError(
        `Total question count (${totalQuestions}) cannot exceed ${MAX_TOTAL_QUESTIONS}. Please reduce the counts.`
      );
      return;
    }

    setIsSubmitting(true);
    setSubmissionProgress("Generating Questions...");
    setProgressPhase("generating");

    // 1. Build the base string and object arrays
    const formattedEntries = newQuestionBank.entries.map((entry) => {
      const hasMCQ = (entry.questionType || []).some(
        (opt) => opt.value && opt.value.toUpperCase().includes("MCQ")
      );
      const formattedEntry = {
        topic: entry.topic,
        subTopic: entry.subTopic,
        difficulty: entry.difficulty.map((option) => option.value).join(", "),
        questionType: entry.questionType.map((option) => option.value).join(", "),
        questionCount: clampQuestionCount(entry.questionCount),
      };
      if (hasMCQ) {
        formattedEntry.mcqSubTypes = (entry.mcqSubTypes || []).map((st) => st.value).join(", ");
      }
      return formattedEntry;
    });

    // 2. Wrap everything inside a FormData instance
    const formData = new FormData();
    formData.append("name", newQuestionBank.name);
    formData.append("customInstructions", newQuestionBank.customInstructions || "");
    formData.append("entries", JSON.stringify(formattedEntries)); // Stringified for Backend Multer Parse

    // Conditionally append source metadata only when source content is provided
    const hasSourceText = (newQuestionBank.source?.texts || []).some((item) => item.trim() !== "");
    const hasSourceLink = (newQuestionBank.source?.links || []).some((item) => item.trim() !== "");
    const hasSourceFiles = Array.isArray(newQuestionBank.source?.files) && newQuestionBank.source.files.length > 0;
    const hasAnySource = hasSourceText || hasSourceLink || hasSourceFiles;

    if (hasAnySource) {
      const sourceMetadata = {
        texts: (newQuestionBank.source?.texts || []).filter((item) => item.trim() !== ""),
        links: (newQuestionBank.source?.links || []).filter((item) => item.trim() !== ""),
        executionMode: newQuestionBank.source?.executionMode || "GENERATE", // other value can be "FETCH"
      };
      formData.append("source", JSON.stringify(sourceMetadata)); // Stringified for Backend Multer Parse
    }

    // 3. Append files into the array field identifier key
    // Assumes your state holds file objects under newQuestionBank.source.files
    if (Array.isArray(newQuestionBank.source?.files)) {
      newQuestionBank.source.files.forEach((fileObject) => {
        formData.append("sourceFiles", fileObject); // Field name match for Multer: upload.array('sourceFiles')
      });
    }

    try {
      // Step 1: Generate Questions
      setSubmissionProgress("Generating Questions...");
      setProgressPhase("generating");
      const response = await fetchWithAuth(
        `${serverUrl}/api/question-bank/create-question-bank/${classId}`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        // Check for 403 status and specific credits exhausted message
        if (
          response.status === 403 &&
          data.message &&
          data.message.includes(
            "You’ve run out of question generation credits. Please contact GradeMe-AI support to purchase additional credits."
          )
        ) {
          swalError(data.message, { timer: 5000, timerProgressBar: true });
          await new Promise((resolve) => setTimeout(resolve, 5000));
          return;
        } else {
          throw new Error(data.message || "Failed to create question bank");
        }
      }

      setSubmissionProgress("Generated Questions...");
      setProgressPhase("completed");

      // Step 2: Generate Solutions if needed
      if (
        newQuestionBank.withSolution &&
        data.questionIds &&
        data.questionIds.length > 0
      ) {
        setSubmissionProgress("Generating Solutions...");
        setProgressPhase("solutions");
        try {
          const solutionResponse = await fetchWithAuth(
            `${serverUrl}/api/question-bank/generate-question-solution`,
            {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ questionIds: data.questionIds }),
            }
          );
          if (solutionResponse && solutionResponse.ok) {
            setSubmissionProgress("Generated Solutions...");
            setProgressPhase("completed");
            // const solutionData = await solutionResponse.json();
            swalSuccess("Question bank with solutions created successfully!");
          } else {
            swalSuccess(
              "Question bank created successfully, but some solutions failed to generate."
            );
          }
        } catch (solutionError) {
          console.error("Solution generation error:", solutionError);
          swalSuccess(
            "Question bank created successfully, but solution generation failed."
          );
        }
      } else {
        swalSuccess("Question bank created successfully.");
      }

      setIsAddQuestionBankModalOpen(false);

      // Reset form
      const questionBankName = newQuestionBank.name;
      setNewQuestionBank({
        name: "",
        customInstructions: "",
        source: {
          texts: [""],
          links: [""],
          executionMode: "GENERATE",
          files: [],
        },
        withSolution: false,
        entries: [
          {
            topic: "",
            subTopic: "",
            difficulty: [],
            questionCount: 5,
            questionType: [],
            mcqSubTypes: [],
          },
        ],
      });

      // Refresh questions and apply filter for newly created question bank
      await fetchQuestions();

      // Apply filter for the newly generated question bank
      setTimeout(() => {
        // Try to trigger filter for the new question bank
        // Prefer using Table ref if available, else fallback to DOM
        if (tableRef.current && tableRef.current.setFilter) {
          tableRef.current.setFilter("questionBankName", questionBankName);
        } else {
          const tableElement = document.querySelector(
            '[data-filter-column="questionBankName"]'
          );
          if (tableElement) {
            const input =
              tableElement.querySelector("input") ||
              tableElement.querySelector("select");
            if (input) {
              input.value = questionBankName;
              input.dispatchEvent(new Event("input", { bubbles: true }));
              input.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }
        }
      }, 500);
    } catch (error) {
      // For credits exhausted error, the error is already shown with timer above
      if (
        !(
          error.message &&
          error.message.includes(
            "You've run out of question generation credits"
          )
        )
      ) {
        swalError(error.message);
      }
    } finally {
      setIsSubmitting(false);
      setSubmissionProgress("");
      setProgressPhase("");
    }
  };

  const generateImage = async (question) => {
    setError("");

    const requestBody = {
      questionId: question._id,
      questionBankId: question.questionBankId,
      questionText: question.questionText,
    };

    try {
      setIsImageLoading((prevState) => ({
        ...prevState,
        [question._id]: true,
      }));
      const response = await fetchWithAuth(
        `${serverUrl}/api/question-bank/create-question-latex`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );
      if (!response) return;

      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to generate LaTeX");

      const updatedQuestion = { ...question, imageURL: data.imageURL, svgCode: data.svgCode };
      const updatedQuestions = questions.map((q) =>
        q._id === question._id ? updatedQuestion : q
      );
      setQuestions(updatedQuestions);
      setImageURL(data.imageURL || null);
      setRegenerateImageQuestion(updatedQuestion);
      showImage(updatedQuestion);
    } catch (error) {
      swalError(error.message);
    } finally {
      setIsImageLoading((prevState) => ({
        ...prevState,
        [question._id]: false,
      }));
    }
  };

  const handleGenerateQuestionPaper = async (options) => {
    setIsExporting(true);
    const questionIds = [...selectedQuestionIds];

    try {
      const safeTitle = (options.paperTitle || "Question_Paper")
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, "_");

      if (options.format === "docx") {
        const response = await fetchWithAuth(
          `${serverUrl}/api/question-bank/generate-docx`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              questionIds,
              exportSolution: options.exportSolution,
            }),
          }
        );

        if (!response) return;
        if (!response.ok) {
          let errMsg = "Failed to export Word document";
          try {
            const errJson = await response.json();
            errMsg = errJson.message || errMsg;
          } catch (_) {}
          throw new Error(errMsg);
        }

        const blob = await response.blob();
        saveAs(blob, `${safeTitle}.docx`);
        swalSuccess("Questions exported as Word document successfully!");
      } else {
        // PDF Export - Question Paper
        const instituteName =
          user?.institute?.name || user?.instituteName || "";
        const instituteLogo =
          user?.institute?.logoURL || user?.institute?.logo || "";

        const qpResponse = await fetchWithAuth(
          `${serverUrl}/api/question-bank/generate-pdf`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              questionIds,
              paperTitle: options.paperTitle || "Question Paper",
              showClass: options.showClass,
              showTopics: options.showTopics,
              instituteName,
              instituteLogo,
            }),
          }
        );

        if (!qpResponse) return;
        if (!qpResponse.ok) {
          let errMsg = "Failed to export Question Paper PDF";
          try {
            const errJson = await qpResponse.json();
            errMsg = errJson.message || errMsg;
          } catch (_) {}
          throw new Error(errMsg);
        }

        const qpBlob = await qpResponse.blob();
        saveAs(qpBlob, `${safeTitle}.pdf`);

        // If solutions requested, fetch and download the separate Solutions PDF as before
        if (options.exportSolution) {
          try {
            const solResponse = await fetchWithAuth(
              `${serverUrl}/api/question-bank/generate-solutions-pdf`,
              {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  questionIds,
                  paperTitle: options.paperTitle || "Question Paper",
                  instituteName,
                  instituteLogo,
                }),
              }
            );

            if (solResponse && solResponse.ok) {
              const solBlob = await solResponse.blob();
              saveAs(solBlob, `${safeTitle}_Solutions.pdf`);
              swalSuccess("Question Paper and Solutions PDFs exported successfully!");
            } else {
              swalSuccess("Question Paper PDF exported successfully!");
            }
          } catch (solErr) {
            console.error("Error generating solutions PDF:", solErr);
            swalSuccess("Question Paper PDF exported successfully!");
          }
        } else {
          swalSuccess("Question Paper PDF exported successfully!");
        }
      }

      setIsGenerateQPModalOpen(false);
    } catch (error) {
      swalError(error.message || "Failed to generate question paper");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportClick = () => {
    if (selectedQuestionIds.size === 0) {
      swalWarning("Please select questions before generating question paper.");
    } else {
      setIsGenerateQPModalOpen(true);
    }
  };

  const handleGenerateSolution = async (questions) => {
    questions.forEach((question) => {
      setIsSolutionLoading((prev) => ({
        ...prev,
        [question._id]: true,
      }));
    });
    const requestBody = {
      questionIds: questions.map((q) => q._id),
    };
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/question-bank/generate-question-solution`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to generate solution");
      }
      const updatedQuestions = data.questions || [];
      updatedQuestions.forEach((question) => {
        setQuestions((prevQuestions) =>
          prevQuestions.map((q) => {
            return q._id === question._id
              ? {
                ...q,
                solution: question.solution,
                correctOptions: question.correctOptions,
              }
              : q;
          })
        );
      });
      if (updatedQuestions[0]) {
        questions[0].solution = updatedQuestions[0].solution; // Update the question object with the new solution
        questions[0].correctOptions = updatedQuestions[0].correctOptions;
      }
      swalSuccess(data.message || "Solution generated successfully.");
    } catch (error) {
      swalError(error.message);
    } finally {
      questions.forEach((question) => {
        setIsSolutionLoading((prev) => ({
          ...prev,
          [question._id]: false,
        }));
      });
      showSolution(questions[0]);
    }
  };

  // ── Delete Question ──
  const handleDeleteQuestion = async (question) => {
    const result = await swalConfirm(
      "Delete Question",
      `Are you sure you want to delete this question? This action cannot be undone.`,
      "<i class='fa-solid fa-trash'></i> Delete",
      "Cancel"
    );
    if (!result.isConfirmed) return;
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/question-bank/delete-question/${question._id}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to delete question");
      swalSuccess("Question deleted successfully.");
      setQuestions((prev) => prev.filter((q) => q._id !== question._id));
      setSelectedQuestionIds((prev) => {
        const next = new Set(prev);
        next.delete(question._id);
        return next;
      });
    } catch (err) {
      swalError(err.message);
    }
  };

  // ── Open Edit Modal ──
  const handleOpenEditModal = (question) => {
    setEditQuestion(question);

    let initialSolution = question.solution || "";

    const formValues = {
      questionText: question.questionText || "",
      options: question.options || [],
      topic: question.topic || "",
      subTopic: question.subTopic || "",
      type: question.type || "",
      difficulty: question.difficulty || "",
      solution: initialSolution,
      correctOptions: Array.isArray(question.correctOptions) ? question.correctOptions.join(", ") : (question.correctOptions || ""),
    };

    setEditForm(formValues);
    setInitialEditForm(formValues);
    setIsEditModalOpen(true);
  };

  // ── Save Edited Question ──
  const handleSaveEditQuestion = async (e) => {
    e.preventDefault();
    if (!editQuestion) return;
    setIsSavingEdit(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/question-bank/update-question/${editQuestion._id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            questionText: editForm.questionText,
            options: editForm.options,
            topic: editForm.topic,
            subTopic: editForm.subTopic,
            type: editForm.type,
            difficulty: editForm.difficulty,
            solution: editForm.solution,
            correctOptions: editForm.correctOptions ? editForm.correctOptions.split(",").map(s => s.trim()).filter(Boolean) : [],
          }),
        }
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update question");
      swalSuccess("Question updated successfully.");
      setQuestions((prev) =>
        prev.map((q) =>
          q._id === editQuestion._id
            ? {
              ...q,
              questionText: editForm.questionText,
              options: editForm.options,
              topic: editForm.topic,
              subTopic: editForm.subTopic,
              type: editForm.type,
              difficulty: editForm.difficulty,
              solution: editForm.solution,
              correctOptions: editForm.correctOptions ? editForm.correctOptions.split(",").map(s => s.trim()).filter(Boolean) : [],
            }
            : q
        )
      );
      setIsEditModalOpen(false);
      setEditQuestion(null);
    } catch (err) {
      swalError(err.message);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const displayedQuestions = useMemo(() => {
    return questions.map((q) => {
      const hasImage = !!(q.imageURL || q.svgCode || q.diagramFileURL);
      const isRecommended = !!q.diagramSuggested;
      let imageFilterStatus = "No Image";
      if (hasImage) {
        imageFilterStatus = "Has Image";
      } else if (isRecommended) {
        imageFilterStatus = "Recommended";
      }
      return {
        ...q,
        imageFilterStatus,
      };
    });
  }, [questions]);

  const questionIndexMap = useMemo(() => {
    const map = new Map();
    displayedQuestions.forEach((q, i) => map.set(q, i));
    return map;
  }, [displayedQuestions]);

  const questionColumns = useMemo(() => [
    {
      key: "Sr. No.",
      label: "#",
      sortable: false,
      render: (question) => {
        return (questionIndexMap.get(question) ?? -1) + 1;
      }
    },
    // Question Bank
    {
      key: "questionBankName",
      label: "Question Bank",
      filter: true,
    },
    {
      key: "topic",
      label: "Topic (Subtopic)",
      filter: true,
      sortable: false,
      render: (question) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", fontSize: "0.88rem" }}>
          <span>
            <span style={{ fontWeight: 600, color: "var(--color-1)" }}>{question.topic || "-"}</span>
          </span>
          {question.subTopic && (
            <span style={{ color: "#6b7280" }}>
              <span style={{ fontWeight: 600 }}>({question.subTopic})</span>
            </span>
          )}
        </div>
      ),
    },
    {
      key: "difficulty",
      label: "Difficulty",
      filter: true,
    },
    {
      key: "type",
      label: "Type",
      filter: true,
      render: (question) => {
        const type = question.type || "";
        const mcqSubType = question.mcqSubType;
        if (mcqSubType) {
          const normalized = mcqSubType
            .replace(/_/g, " ")
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
          return `${type} (${normalized})`;
        }
        return type;
      },
    },
    {
      key: "questionText",
      label: "Question",
      sortable: false,
      filter: false,
      render: (question) => {
        return (
          <div className="question-text-container">
            <div className="question-text" style={{ fontSize: "0.97rem", lineHeight: 1.7 }}>
              <div
                className="question-preview"
                onClick={(e) => { e.stopPropagation(); showQuestion(question); }}
                style={{
                  cursor: "pointer",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
                title="Click to view full question"
              >
                <LazyMarkdown>{question.questionText || ""}</LazyMarkdown>
              </div>
            </div>
          </div>
        );
      },
    },

    // Source
    {
      key: "source",
      label: "Source",
      filter: true,
      sortable: true,
      render: (question) => {
        const source = question.source;
        if (!source) return <span style={{ color: "#94a3b8", fontSize: "0.85rem" }}>—</span>;
        return (
          <span
            title={source}
            style={{
              display: "block",
              maxWidth: "120px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "0.93rem",
              color: "inherit",
              cursor: "pointer",
            }}
          >
            {source}
          </span>
        );
      },
    },

    // Image column (Allows AI generation or local upload)
    {
      key: "imageFilterStatus",
      label: "Image",
      filter: true,
      sortable: true,
      sortBy: (question) => {
        const hasImage = !!(question.imageURL || question.svgCode || question.diagramFileURL);
        const isRecommended = question.diagramSuggested;
        if (hasImage) return 0;
        if (isRecommended) return 1;
        return 2;
      },
      render: (question) => {
        const isRecommended = question.diagramSuggested;
        const hasImage = !!(question.imageURL || question.svgCode || question.diagramFileURL);

        return (
          <div className="image-btn-wrapper">
            {isRecommended && !hasImage && !isImageLoading[question._id] && (
              <span className="ai-recommended-badge" title="AI suggests this question would benefit from a diagram">
                Recommended
              </span>
            )}
            <button
              className="view-btn"
              style={{
                backgroundColor: hasImage ? "var(--color-1)" : "#6bcb77",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                borderRadius: "6px",
                color: "white",
                border: "none",
                fontWeight: "600",
                fontSize: "0.85rem",
              }}
              onClick={() => {
                setSelectedImageQuestion(question);
                setDiagramQuestion(question);
                setIsDiagramImageModalOpen(true);
              }}
              disabled={isImageLoading[question._id]}
            >
              {isImageLoading[question._id] ? (
                <Loader fontSize="20px" style={{ left: "0", top: "0" }} />
              ) : hasImage ? (
                <>
                  <i className="fa-regular fa-image" />
                  View
                </>
              ) : (
                <>
                  <i className="fa-solid fa-plus" />
                  Add
                </>
              )}
            </button>
          </div>
        );
      },
    },

    // Solution
    {
      key: "solution",
      label: "Solution",
      sortable: false,
      filter: false,
      render: (question) => {
        const hasSolution = question.solution;
        return (
          <>
            {hasSolution ? (
              <button
                onClick={() => showSolution(question)}
                className="view-btn"
                style={{ backgroundColor: "var(--color-1)", cursor: "pointer" }}
              >
                <i className="fa-regular fa-eye" />
              </button>
            ) : (
              <button
                onClick={() => handleGenerateSolution([question])}
                className="view-btn"
                style={{ cursor: "pointer" }}
              >
                {isSolutionLoading[question._id] ? (
                  <Loader fontSize="20px" style={{ left: "0", top: "0" }} />
                ) : (
                  "Generate"
                )}
              </button>
            )}
          </>
        );
      },
    },
    // Actions – Edit / Delete
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      filter: false,
      render: (question) => (
        <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
          <button
            className="edit-btn"
            title="Edit question"
            onClick={() => handleOpenEditModal(question)}
          >
            <i className="fa-solid fa-pen-to-square" />
          </button>
          <button
            className="delete-btn"
            title="Delete question"
            onClick={() => handleDeleteQuestion(question)}
          >
            <i className="fa-solid fa-trash" />
          </button>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [questionIndexMap, isImageLoading, isSolutionLoading]);

  const handleCheckboxSelect = useCallback((e, questionId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedQuestionIds((prevIds) => {
      const newIds = new Set(prevIds);
      if (newIds.has(questionId)) {
        newIds.delete(questionId);
      } else {
        newIds.add(questionId);
      }
      return newIds;
    });
  }, []);

  const handleSelectAll = useCallback((visibleItemIds, isCurrentlyChecked) => {
    setSelectedQuestionIds((prevIds) => {
      const newIds = new Set(prevIds);
      if (isCurrentlyChecked) {
        visibleItemIds.forEach((id) => newIds.delete(id));
      } else {
        visibleItemIds.forEach((id) => newIds.add(id));
      }
      return newIds;
    });
  }, []);

  // Add function to calculate total questions
  const calculateTotalQuestions = (entries) => {
    return entries.reduce((total, entry) => {
      const count = parseInt(entry.questionCount) || 0;
      return total + count;
    }, 0);
  };

  const hasSourceText = (newQuestionBank.source?.texts || []).some((item) => item.trim() !== "");
  const hasSourceLink = (newQuestionBank.source?.links || []).some((item) => item.trim() !== "");
  const hasSourceFiles = Array.isArray(newQuestionBank.source?.files) && newQuestionBank.source.files.length > 0;
  const hasAnySource = hasSourceText || hasSourceLink || hasSourceFiles;

  const isTextbookDisabled = hasSourceLink || hasSourceFiles;
  const isLinkDisabled = hasSourceText || hasSourceFiles;
  const isPdfDisabled = hasSourceText || hasSourceLink;

  const ImageWithErrorHandler = ({ src, alt, style }) => {
    const [hasError, setHasError] = useState(false);

    if (hasError || !src) {
      const fileName = src?.split("/").pop() || "image.png";
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px 24px",
            background: "#f8fafc",
            border: "1.5px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px 16px",
            alignItems: "center",
            width: "100%",
            minHeight: "80px",
            ...style,
          }}
        >
          <div style={{ display: "flex", gap: "8px", alignItems: "center", fontSize: "0.92rem", color: "#6b7280" }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ color: "#ef4444", fontSize: "18px" }} />
            <span>
              Cannot read <strong>"{fileName}"</strong>
            </span>
          </div>
          <div style={{ fontSize: "0.88rem", color: "#9ca3af", textAlign: "right" }}>
            This model does not support image input
          </div>
        </div>
      );
    }

    return (
      <img
        src={src}
        alt={alt}
        style={style}
        onError={() => setHasError(true)}
      />
    );
  };

  const handleGenerateImageFromModal = async () => {
    if (!selectedImageQuestion) return;
    setIsImageOptionsModalOpen(false);
    generateImage(selectedImageQuestion);
  };

  const handleUploadDiagramFile = async () => {
    if (!selectedUploadFile || !selectedImageQuestion) return;

    try {
      setIsUploadingFile(true);
      const formData = new FormData();
      formData.append("image", selectedUploadFile);

      const endpoint = `${serverUrl}/api/question-bank/new-question-diagram/${selectedImageQuestion._id}`;
      const response = await fetchWithAuth(endpoint, {
        method: "POST",
        body: formData,
      });

      if (!response) {
        setIsUploadingFile(false);
        return;
      }

      const data = await response.json();
      if (response.ok) {
        //swalSuccess(data.message || "Question diagram updated successfully");

        const updatedQuestion = {
          ...selectedImageQuestion,
          diagramPageFileURL: data.diagramPageFileURL,
          diagramFileURL: null,
          diagramBoundingBox: null,
        };

        setQuestions((prevQuestions) =>
          prevQuestions.map((q) =>
            q._id === updatedQuestion._id ? updatedQuestion : q
          )
        );

        setExtractedQuestions((prevExtracted) =>
          prevExtracted.map((q) =>
            q._id === selectedImageQuestion._id || q.questionId === selectedImageQuestion._id
              ? { ...q, diagramPageFileURL: data.diagramPageFileURL, diagramFileURL: null, diagramBoundingBox: null }
              : q
          )
        );

        setDiagramQuestion(updatedQuestion);
        setIsFileUploadModalOpen(false);
        setIsImageOptionsModalOpen(false);
        setSelectedUploadFile(null);
        setIsCropModalOpen(true);
      } else {
        swalError(data.message || "Failed to upload question diagram");
      }
    } catch (err) {
      console.error("Error uploading diagram:", err);
      swalError("Failed to upload question diagram.");
    } finally {
      setIsUploadingFile(false);
    }
  };

  const isFormChanged =
    editForm.questionText !== initialEditForm.questionText ||
    JSON.stringify(editForm.options) !== JSON.stringify(initialEditForm.options) ||
    editForm.topic !== initialEditForm.topic ||
    editForm.subTopic !== initialEditForm.subTopic ||
    editForm.type !== initialEditForm.type ||
    editForm.difficulty !== initialEditForm.difficulty ||
    editForm.solution !== initialEditForm.solution ||
    editForm.correctOptions !== initialEditForm.correctOptions;

  return (
    <>
      {isCapturing ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            background: "white",
          }}
        >
          <MobileCapture
            onComplete={onCaptureComplete}
            onClose={handleCloseCapture}
            captureType={captureFor === "upload-diagram" ? "image" : "pdf"}
          />
        </div>
      ) : (
        <>
          <div className="manage-container">
            {loading ? (
              <Loader />
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : (
              <div className="questions-view">
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginBottom: "1rem",
                    float: "right",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    className="create-btn"
                    onClick={() => setIsQBTypeModalOpen(true)}
                    title="Create a new set of questions"
                  >
                    Create Question Bank <i className="fa-solid fa-plus"></i>
                  </button>

                  <button
                    className="create-btn"
                    onClick={handleExportClick}
                    title={
                      selectedQuestionIds.size > 0
                        ? `Generate Question Paper from ${selectedQuestionIds.size} selected questions`
                        : "Select questions to generate question paper"
                    }
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: "600",
                      opacity: selectedQuestionIds.size > 0 ? 1 : 0.5,
                    }}
                  >
                    <i className="fa-solid fa-file-pdf"></i>
                    Generate Question Paper
                    {selectedQuestionIds.size > 0 && (
                      <span
                        style={{
                          background: "rgba(255, 255, 255, 0.25)",
                          padding: "1px 7px",
                          borderRadius: "12px",
                          fontSize: "12px",
                        }}
                      >
                        {selectedQuestionIds.size}
                      </span>
                    )}
                  </button>
                </div>
                <Table
                  ref={tableRef}
                  data={displayedQuestions}
                  columns={questionColumns}
                  caption={`Questions ${selectedQuestionIds.size > 0
                    ? `(${selectedQuestionIds.size} Selected)`
                    : ""
                    }`}
                  selectedRowIds={selectedQuestionIds}
                  onRowSelect={handleCheckboxSelect}
                  onSelectAll={handleSelectAll}
                  isCheckBoxAvailable={true}
                />
              </div>
            )}
          </div>

          {/* ── Generate Question Paper Modal ── */}
          <GenerateQuestionPaperModal
            isOpen={isGenerateQPModalOpen}
            onClose={() => setIsGenerateQPModalOpen(false)}
            selectedCount={selectedQuestionIds.size}
            classDetails={classDetails}
            topics={selectedTopics}
            onGenerate={handleGenerateQuestionPaper}
            isGenerating={isExporting}
          />

          {/* ── QB Type Selection Modal ── */}
          <Modal
            isOpen={isQBTypeModalOpen}
            onClose={() => setIsQBTypeModalOpen(false)}
            maxWidth="520px"
          >
            <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📚</div>
              <h2 style={{ fontSize: "1.4rem", fontWeight: 700, margin: 0 }}>Generate Question Bank</h2>
              <p style={{ color: "#6b7280", marginTop: "0.4rem", fontSize: "0.95rem" }}>Select how you want to create this question bank.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              {/* Option 1: AI Question Bank */}
              <button
                onClick={() => {
                  setIsQBTypeModalOpen(false);
                  setIsAddQuestionBankModalOpen(true);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  padding: "18px 20px",
                  background: "white",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: "14px",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  width: "100%",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-1)";
                  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(240,154,33,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{
                  width: "52px", height: "52px", borderRadius: "12px",
                  background: "linear-gradient(135deg, #f09a21 0%, #d97706 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0
                }}>
                  <i className="fa-solid fa-robot" style={{ color: "white", fontSize: "1.4rem" }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827", marginBottom: "3px" }}>AI Question Bank</div>
                  <div style={{ fontSize: "0.86rem", color: "#6b7280", lineHeight: 1.5 }}>Generate questions using AI based on topic, difficulty, and type settings.</div>
                </div>
                <i className="fa-solid fa-chevron-right" style={{ color: "#9ca3af", fontSize: "0.9rem" }}></i>
              </button>

              {/* Option 2: PDF Extractor */}
              <button
                onClick={() => {
                  setIsQBTypeModalOpen(false);
                  handleFromTestPaperClick();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  padding: "18px 20px",
                  background: "white",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: "14px",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  width: "100%",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-1)";
                  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(240,154,33,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{
                  width: "52px", height: "52px", borderRadius: "12px",
                  background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0
                }}>
                  <i className="fa-solid fa-file-pdf" style={{ color: "white", fontSize: "1.4rem" }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827", marginBottom: "3px" }}>PDF Extractor</div>
                  <div style={{ fontSize: "0.86rem", color: "#6b7280", lineHeight: 1.5 }}>Convert a test paper PDF into a structured question bank automatically.</div>
                </div>
                <i className="fa-solid fa-chevron-right" style={{ color: "#9ca3af", fontSize: "0.9rem" }}></i>
              </button>

            </div>
          </Modal>

          <Modal
            isOpen={isSolutionModalOpen}
            onClose={() => setIsSolutionModalOpen(false)}
          >
            {!(activeQuestion?.solution || activeQuestion?.correctOptions?.length > 0) ? (
              <p>No Solution Available. Retry Generating.</p>
            ) : (
              <>
                <h2>Solution Details</h2>
                <div
                  className="solution-content"
                  style={{
                    background: "#f8fafc",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    fontSize: "0.92rem",
                    marginTop: "1rem",
                    marginBottom: "1rem",
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap"
                  }}
                >
                  {/* Correct Options for MCQ */}
                  {activeQuestion?.type?.toUpperCase() === "MCQ" &&
                    Array.isArray(activeQuestion.correctOptions) &&
                    activeQuestion.correctOptions.length > 0 && (
                      <div style={{ marginBottom: activeQuestion.solution ? "16px" : "0" }}>
                        <strong>Correct Option: </strong>
                        {activeQuestion.correctOptions.join(", ")}
                      </div>
                    )}

                  {/* <p>{solution}</p> */}
                  {activeQuestion?.solution && (
                    <div>
                      <strong>Solution: </strong>
                      <div style={{ marginTop: "8px" }}>
                        <ReactMarkdown
                          children={activeQuestion.solution.replace(/\\\(/g, "$").replace(/\\\)/g, "$").replace(/\\\[/g, "$$").replace(/\\\]/g, "$$")}
                          remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                          rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-actions">
                  <button
                    className="btn-submit"
                    onClick={() => copyToClipboard(activeQuestion?.solution)}
                    title="Copy solution to clipboard"
                  >
                    <i className="fa-solid fa-copy"></i> Copy Solution
                  </button>
                </div>
              </>
            )}
          </Modal>

          <Modal
            isOpen={isImageModalOpen}
            onClose={() => setIsImageModalOpen(false)}
          >
            <h2>Image</h2>
            <div className="modal-actions">
              {regenerateImageQuestion?.svgCode ? (
                <button
                  className="btn-submit"
                  onClick={() => copyToClipboard(regenerateImageQuestion.svgCode)}
                  title="Copy SVG to clipboard"
                >
                  <i className="fa-solid fa-copy"></i> Copy SVG
                </button>
              ) : imageURL ? (
                <button
                  className="btn-submit"
                  onClick={() => copyImageToClipboard(imageURL)}
                  title="Copy image to clipboard"
                >
                  <i className="fa-solid fa-copy"></i> Copy Image
                </button>
              ) : null}
              {ImageAllow && (
                <button
                  className="btn-submit"
                  onClick={() => generateImage(regenerateImageQuestion)}
                  disabled={isImageLoading[regenerateImageQuestion?._id]}
                >
                  {isImageLoading[regenerateImageQuestion?._id] ? (
                    <Loader fontSize="20px" style={{ left: "0", top: "0" }} />
                  ) : (
                    "Regenerate Image"
                  )}
                </button>
              )}
            </div>
            <div className="image-content" style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", width: "100%", minHeight: "200px" }}>
              <style>{`
            .image-content svg {
              max-width: 100%;
              height: auto;
            }
          `}</style>
              {regenerateImageQuestion?.svgCode ? (
                <div
                  dangerouslySetInnerHTML={{ __html: regenerateImageQuestion.svgCode }}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                />
              ) : imageURL ? (
                <ImageWithErrorHandler src={imageURL} alt="Question Visualization" style={{ maxWidth: "100%", height: "auto" }} />
              ) : null}
            </div>
          </Modal>

          {/* ── Diagram Image Viewer (supports SVG + diagramFileURL) ── */}
          <Modal
            isOpen={isDiagramImageModalOpen}
            onClose={() => {
              setIsDiagramImageModalOpen(false);
              setDiagramQuestion(null);
              setSelectedImageQuestion(null);
            }}
            maxWidth="460px"
          >
            <h2 style={{ marginBottom: "0.8rem" }}>
              <i className="fa-regular fa-image" style={{ marginRight: "8px", color: "var(--color-1)" }} />
              Question Diagram
            </h2>

            {/* Image / SVG display */}
            <style>{`
          .qd-svg-wrapper svg {
            max-width: 100% !important;
            max-height: 260px !important;
            height: auto !important;
            width: auto !important;
          }
        `}</style>
            {(diagramQuestion?.svgCode || diagramQuestion?.imageURL || diagramQuestion?.diagramFileURL) && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  maxHeight: "300px",
                  background: "#f8fafc",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "2px solid #e2e8f0",
                  marginBottom: "1rem",
                }}
              >
                {diagramQuestion.svgCode ? (
                  <div
                    className="qd-svg-wrapper"
                    dangerouslySetInnerHTML={{ __html: diagramQuestion.svgCode }}
                    style={{
                      width: "100%",
                      maxHeight: "260px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "16px",
                      overflow: "hidden",
                    }}
                  />
                ) : diagramQuestion.imageURL ? (
                  <ImageWithErrorHandler
                    src={diagramQuestion.imageURL}
                    alt="Question Diagram"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "260px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : diagramQuestion.diagramFileURL ? (
                  <ImageWithErrorHandler
                    src={diagramQuestion.diagramFileURL}
                    alt="Question Diagram"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "260px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : null}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: "flex", flexDirection: "row", gap: "0.6rem", marginTop: "1rem" }}>
              {/* Copy SVG — only when SVG code is present */}
              {diagramQuestion?.svgCode && (
                <button
                  type="button"
                  className="btn-submit"
                  onClick={() => copyToClipboard(diagramQuestion.svgCode)}
                  title="Copy SVG code to clipboard"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    padding: "12px 6px",
                    background: "linear-gradient(45deg, black, grey)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.78rem",
                    lineHeight: 1.3,
                    transition: "opacity 0.2s",
                    textAlign: "center",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  <i className="fa-solid fa-copy" style={{ fontSize: "1.15rem" }} />
                  Copy SVG
                </button>
              )}

              {/* Regenerate AI Image */}
              {ImageAllow && (
                <button
                  type="button"
                  className="btn-submit"
                  onClick={() => {
                    const q = diagramQuestion;
                    setIsDiagramImageModalOpen(false);
                    generateImage(q);
                  }}
                  disabled={isImageLoading[diagramQuestion?._id]}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    padding: "12px 6px",
                    background: "linear-gradient(45deg, black, grey)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.78rem",
                    lineHeight: 1.3,
                    transition: "opacity 0.2s",
                    textAlign: "center",
                    opacity: isImageLoading[diagramQuestion?._id] ? 0.7 : 1,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  {isImageLoading[diagramQuestion?._id] ? (
                    <Loader fontSize="20px" style={{ left: "0", top: "0" }} />
                  ) : (
                    <>
                      <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: "1.15rem" }} />
                      Regenerate AI Image
                    </>
                  )}
                </button>
              )}

              {/* Upload Replacement */}
              <button
                type="button"
                className="btn-submit"
                onClick={() => {
                  setSelectedImageQuestion(diagramQuestion);
                  setIsFileUploadModalOpen(true);
                }}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  padding: "12px 6px",
                  background: "linear-gradient(45deg, black, grey)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "0.78rem",
                  lineHeight: 1.3,
                  transition: "opacity 0.2s",
                  textAlign: "center",
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: "1.15rem" }} />
                {(diagramQuestion?.svgCode || diagramQuestion?.imageURL || diagramQuestion?.diagramFileURL) ? "Upload Replacement" : "Upload Diagram"}
              </button>

              {/* Resize / Recrop — when diagramPageFileURL or diagramFileURL/imageURL exists */}
              {(diagramQuestion?.diagramPageFileURL || diagramQuestion?.diagramFileURL || diagramQuestion?.imageURL) && diagramQuestion?.source !== "library" && (
                <button
                  type="button"
                  className="btn-submit"
                  onClick={() => {
                    setIsCropModalOpen(true);
                  }}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    padding: "12px 6px",
                    background: "linear-gradient(45deg, black, grey)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.78rem",
                    lineHeight: 1.3,
                    transition: "opacity 0.2s",
                    textAlign: "center",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  <i className="fa-solid fa-crop-simple" style={{ fontSize: "1.15rem" }} />
                  Resize Diagram
                </button>
              )}
            </div>
          </Modal>

          {/* ── Image Options Modal ── */}
          <Modal
            isOpen={isImageOptionsModalOpen}
            onClose={() => {
              setIsImageOptionsModalOpen(false);
              setSelectedImageQuestion(null);
            }}
            maxWidth="420px"
          >
            <h2 style={{ marginBottom: "1.2rem", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="fa-regular fa-image" style={{ color: "var(--color-1)" }}></i>
              {selectedImageQuestion?.imageURL || selectedImageQuestion?.svgCode || selectedImageQuestion?.diagramFileURL ? "Question Diagram" : "Add Question Diagram"}
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {/* If image exists, show preview */}
              {(selectedImageQuestion?.imageURL || selectedImageQuestion?.svgCode || selectedImageQuestion?.diagramFileURL) && (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  background: "#f8fafc",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "0.25rem",
                  maxHeight: "280px",
                  overflow: "hidden"
                }}>
                  {selectedImageQuestion.svgCode ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: selectedImageQuestion.svgCode }}
                      style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
                    />
                  ) : (
                    <ImageWithErrorHandler
                      src={selectedImageQuestion.diagramFileURL || selectedImageQuestion.imageURL}
                      alt="Question Visualization"
                      style={{ maxWidth: "100%", maxHeight: "240px", objectFit: "contain" }}
                    />
                  )}
                </div>
              )}

              {/* Buttons — always a single row */}
              <div style={{ display: "flex", flexDirection: "row", gap: "0.6rem" }}>
                {/* Option 1: Generate / Regenerate AI Image */}
                <button
                  onClick={handleGenerateImageFromModal}
                  className="btn-submit"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    padding: "12px 6px",
                    background: "linear-gradient(135deg, #f09a21 0%, #d97706 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.78rem",
                    lineHeight: 1.3,
                    transition: "opacity 0.2s",
                    textAlign: "center",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: "1.15rem" }}></i>
                  {(selectedImageQuestion?.imageURL || selectedImageQuestion?.svgCode || selectedImageQuestion?.diagramFileURL) ? "Regenerate AI Image" : "Generate AI Image"}
                </button>

                {/* Option 2: Upload */}
                <button
                  onClick={() => setIsFileUploadModalOpen(true)}
                  className="btn-submit"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    padding: "12px 6px",
                    background: "linear-gradient(135deg, #f09a21 0%, #d97706 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.78rem",
                    lineHeight: 1.3,
                    transition: "opacity 0.2s",
                    textAlign: "center",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: "1.15rem" }}></i>
                  {(selectedImageQuestion?.imageURL || selectedImageQuestion?.svgCode || selectedImageQuestion?.diagramFileURL) ? "Upload Replacement" : "Upload Diagram Image"}
                </button>

                {/* Option 3: Resize/Recrop — when diagramPageFileURL or diagramFileURL/imageURL exists */}
                {(selectedImageQuestion?.diagramPageFileURL || selectedImageQuestion?.diagramFileURL || selectedImageQuestion?.imageURL) && selectedImageQuestion?.source !== "library" && (
                  <button
                    onClick={() => {
                      setDiagramQuestion(selectedImageQuestion);
                      setIsImageOptionsModalOpen(false);
                      setIsCropModalOpen(true);
                    }}
                    className="btn-submit"
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "5px",
                      padding: "12px 6px",
                      background: "var(--color-1)",
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "0.78rem",
                      lineHeight: 1.3,
                      transition: "opacity 0.2s",
                      textAlign: "center",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                  >
                    <i className="fa-solid fa-crop-simple" style={{ fontSize: "1.15rem" }}></i>
                    Resize / Recrop
                  </button>
                )}
              </div>
            </div>
          </Modal>

          {/* ── File Upload Modal ── */}
          <Modal
            isOpen={isFileUploadModalOpen}
            onClose={() => {
              setIsFileUploadModalOpen(false);
              setSelectedUploadFile(null);
            }}
            maxWidth="500px"
          >
            <h2 style={{ marginBottom: "1.2rem", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="fa-solid fa-cloud-arrow-up" style={{ color: "var(--color-1)" }}></i>
              Upload Diagram Image
            </h2>
            <div style={{ margin: "1.5rem 0" }}>
              <FileUploadSection
                currentFileUrl={null}
                currentFileName={selectedUploadFile ? selectedUploadFile.name : null}
                isSubmitting={isUploadingFile}
                accept="image/*"
                browseDescription="Select image file from device"
                captureDescription="Take photo of diagram"
                selectedFile={selectedUploadFile}
                onFileChange={(file) => {
                  setSelectedUploadFile(file);
                }}
                onCapture={(callback) => {
                  setCaptureFor("upload-diagram");
                  setIsCapturing(true);
                  window.__fileUploadCallback = callback;
                }}
                onRemoveSelectedFile={() => {
                  setSelectedUploadFile(null);
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1rem" }}>
              <button
                className="btn-submit"
                style={{
                  background: "white",
                  color: "#475569",
                  border: "1px solid #cbd5e1"
                }}
                onClick={() => {
                  setIsFileUploadModalOpen(false);
                  setSelectedUploadFile(null);
                }}
                disabled={isUploadingFile}
              >
                Cancel
              </button>
              <button
                className="btn-submit"
                style={{
                  background: isUploadingFile ? "#94a3b8" : "var(--color-1)",
                  color: "white"
                }}
                onClick={handleUploadDiagramFile}
                disabled={!selectedUploadFile || isUploadingFile}
              >
                {isUploadingFile ? "Uploading..." : "Upload & Crop"}
              </button>
            </div>
          </Modal>

          {/* ── Diagram Image Crop Modal ── */}
          <QuestionDiagramResizeModal
            isOpen={isCropModalOpen}
            onClose={() => {
              setIsCropModalOpen(false);
            }}
            question={diagramQuestion}
            serverUrl={serverUrl}
            onSaveSuccess={handleDiagramSaveSuccess}
          />

          <Modal
            isOpen={isQuestionModalOpen}
            onClose={() => setIsQuestionModalOpen(false)}
            maxWidth="660px"
          >
            {/* ── Question ── */}
            <div style={{ marginBottom: "1.2rem" }}>
              <p style={{ fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", color: "var(--color-1)", letterSpacing: "0.08em", marginBottom: "6px" }}>
                Question
              </p>
              <div style={{ fontSize: "0.97rem", lineHeight: 1.7 }}>
                <ReactMarkdown
                  children={selectedQuestion?.questionText || ""}
                  remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                  rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                />
              </div>
            </div>
            <hr style={{ margin: "1rem 0", border: "none", borderTop: "1.5px solid #e2e8f0" }} />

            {/* ── Options (MCQ 2-column grid) ── */}
            {selectedQuestion &&
              Array.isArray(selectedQuestion.options) &&
              selectedQuestion.options.filter(Boolean).length > 0 && (
                <>
                  <div style={{ marginBottom: "1.2rem" }}>
                    <p style={{ fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", color: "var(--color-1)", letterSpacing: "0.08em", marginBottom: "8px" }}>
                      Options
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px 24px",
                        background: "#f8fafc",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "8px",
                        padding: "12px 16px",
                      }}
                    >
                      {selectedQuestion.options.filter(Boolean).map((opt) => (
                        <div key={opt.key} style={{ display: "flex", gap: "8px", alignItems: "flex-start", fontSize: "0.92rem" }}>
                          <span style={{ fontWeight: 700, minWidth: "20px", color: "var(--color-1)" }}>{opt.key})</span>
                          <div style={{ flex: 1 }}>
                            <ReactMarkdown
                              children={opt.text}
                              remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                              rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <hr style={{ margin: "1rem 0", border: "none", borderTop: "1.5px solid #e2e8f0" }} />
                </>
              )}

            {/* ── Correct Option (shown after options, before solution) ── */}
            {selectedQuestion &&
              selectedQuestion?.type?.toUpperCase() === "MCQ" &&
              Array.isArray(selectedQuestion.correctOptions) &&
              selectedQuestion.correctOptions.length > 0 && (
                <>
                  <div style={{ marginBottom: "1.2rem" }}>
                    <p style={{ fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", color: "var(--color-1)", letterSpacing: "0.08em", marginBottom: "8px" }}>
                      Correct Option
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px 24px",
                        background: "#f8fafc",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "8px",
                        padding: "12px 16px",
                      }}
                    >
                      {selectedQuestion.options
                        ?.filter((opt) => opt && opt.key && selectedQuestion.correctOptions.includes(opt.key))
                        .map((opt) => (
                          <div key={opt.key} style={{ display: "flex", gap: "8px", alignItems: "flex-start", fontSize: "0.92rem" }}>
                            <span style={{ fontWeight: 700, minWidth: "20px", color: "var(--color-1)" }}>{opt.key})</span>
                            <div style={{ flex: 1 }}>
                              <ReactMarkdown
                                children={opt.text}
                                remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                  <hr style={{ margin: "1rem 0", border: "none", borderTop: "1.5px solid #e2e8f0" }} />
                </>
              )}

            {/* ── Solution ── */}
            {selectedQuestion && selectedQuestion.solution && (
              <>
                <div style={{ marginBottom: "1.2rem" }}>
                  <p style={{ fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", color: "var(--color-1)", letterSpacing: "0.08em", marginBottom: "8px" }}>
                    Solution
                  </p>
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      fontSize: "0.92rem",
                      lineHeight: 1.7,
                      whiteSpace: "pre-wrap"
                    }}
                  >
                    <ReactMarkdown
                      children={selectedQuestion.solution.replace(/\\\(/g, "$").replace(/\\\)/g, "$").replace(/\\\[/g, "$$").replace(/\\\]/g, "$$")}
                      remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                      rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                    />
                  </div>
                </div>
                <hr style={{ margin: "1rem 0", border: "none", borderTop: "1.5px solid #e2e8f0" }} />
              </>
            )}

            {/* ── Meta: Type, Difficulty & Source ── */}
            <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
              {selectedQuestion?.source && (
                <div>
                  <span style={{ fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", color: "var(--color-1)", letterSpacing: "0.08em" }}>Source: </span>
                  <span style={{ fontSize: "0.93rem" }}>{selectedQuestion.source}</span>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="btn-submit"
                onClick={() => {
                  const optionsText = Array.isArray(selectedQuestion?.options) && selectedQuestion.options.filter(Boolean).length > 0
                    ? "\n" + selectedQuestion.options.filter(Boolean).map(opt => `${opt.key}. ${opt.text}`).join("\n")
                    : "";
                  copyToClipboard(selectedQuestion?.questionText + optionsText);
                }}
                title="Copy question to clipboard"
              >
                <i className="fa-solid fa-copy" /> Copy Question
              </button>
            </div>
          </Modal>

          {/* ── Edit Question Modal ── */}
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => {
              if (!isSavingEdit) {
                setIsEditModalOpen(false);
                setEditQuestion(null);
              }
            }}
            maxWidth="680px"
          >
            <h2 style={{ marginBottom: "1.2rem" }}>Edit Question</h2>
            <form onSubmit={handleSaveEditQuestion} className="rubric-form">

              {/* Question Text */}
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>
                  <i className="fa-solid fa-question" style={{ marginRight: "6px", color: "var(--color-1)" }} />
                  Question Text
                </label>
                <textarea
                  ref={(el) => {
                    if (el) {
                      el.style.height = "auto";
                      el.style.height = el.scrollHeight + 5 + "px";
                    }
                  }}
                  value={editForm.questionText}
                  onChange={(e) => {
                    setEditForm((p) => ({ ...p, questionText: e.target.value }));
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + 5 + "px";
                  }}
                  placeholder="Enter question text..."
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "2px solid var(--color-8, #e2e8f0)",
                    fontSize: "0.95rem",
                    resize: "vertical",
                    fontFamily: "inherit",
                    lineHeight: 1.6,
                    minHeight: "120px",
                    overflow: "hidden"
                  }}
                />
              </div>

              {/* Options – Editable view */}
              {editForm &&
                Array.isArray(editForm.options) &&
                editForm.options.length > 0 && (
                  <div className="form-group">
                    <label style={{ fontWeight: 600 }}>
                      <i
                        className="fa-solid fa-list-ul"
                        style={{ marginRight: "6px", color: "var(--color-1)" }}
                      />
                      Options
                    </label>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        marginTop: "0.5rem",
                      }}
                    >
                      {editForm.options.map((opt, index) => {
                        if (!opt) return null;
                        return (
                          <div
                            key={opt.key || index}
                            style={{
                              display: "flex",
                              gap: "10px",
                              alignItems: "flex-start",
                              fontSize: "0.9rem",
                              color: "#374151",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 700,
                                minWidth: "20px",
                                marginTop: "10px",
                              }}
                            >
                              {opt.key}.
                            </span>
                            <input
                              type="text"
                              value={opt.text || ""}
                              onChange={(e) => {
                                const newOptions = [...editForm.options];
                                newOptions[index] = {
                                  ...newOptions[index],
                                  text: e.target.value,
                                };
                                setEditForm((prev) => ({
                                  ...prev,
                                  options: newOptions,
                                }));
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                  e.preventDefault();
                                  handleSaveEditQuestion(e);
                                }
                              }}
                              placeholder={`Option ${opt.key} text...`}
                              style={{
                                width: "100%",
                                padding: "9px 12px",
                                borderRadius: "8px",
                                border: "2px solid var(--color-8, #e2e8f0)",
                                fontSize: "0.9rem",
                                fontFamily: "inherit",
                                background: "white",
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Topic & Subtopic row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600 }}>
                    <i className="fa-solid fa-tag" style={{ marginRight: "6px", color: "var(--color-1)" }} />
                    Topic
                  </label>
                  <input
                    type="text"
                    value={editForm.topic}
                    onChange={(e) => setEditForm((p) => ({ ...p, topic: e.target.value }))}
                    placeholder="e.g. Linear Motion"
                    required
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600 }}>
                    <i className="fa-solid fa-bookmark" style={{ marginRight: "6px", color: "var(--color-1)" }} />
                    Subtopic
                  </label>
                  <input
                    type="text"
                    value={editForm.subTopic}
                    onChange={(e) => setEditForm((p) => ({ ...p, subTopic: e.target.value }))}
                    placeholder="e.g. Dimensions"
                  />
                </div>
              </div>

              {/* Type & Difficulty row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600 }}>
                    <i className="fa-solid fa-layer-group" style={{ marginRight: "6px", color: "var(--color-1)" }} />
                    Type
                  </label>
                  <select
                    value={editForm.type}
                    onChange={(e) => setEditForm((p) => ({ ...p, type: e.target.value }))}
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: "8px",
                      border: "2px solid var(--color-8, #e2e8f0)",
                      fontSize: "0.9rem",
                      background: "white",
                    }}
                  >
                    <option value="">-- Select Type --</option>
                    {questionTypeOptions.map((opt) => (
                      <option key={opt._id || opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600 }}>
                    <i className="fa-solid fa-gauge" style={{ marginRight: "6px", color: "var(--color-1)" }} />
                    Difficulty
                  </label>
                  <select
                    value={editForm.difficulty}
                    onChange={(e) => setEditForm((p) => ({ ...p, difficulty: e.target.value }))}
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: "8px",
                      border: "2px solid var(--color-8, #e2e8f0)",
                      fontSize: "0.9rem",
                      background: "white",
                    }}
                  >
                    <option value="">-- Select Difficulty --</option>
                    {difficultyOptions.map((opt) => (
                      <option key={opt._id || opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Solution & Correct Options */}
              {editForm.type?.toUpperCase() === "MCQ" && (
                <div className="form-group" style={{ marginTop: "1rem", marginBottom: "0" }}>
                  <label style={{ fontWeight: 600 }}>
                    <i className="fa-solid fa-check-circle" style={{ marginRight: "6px", color: "var(--color-1)" }} />
                    Correct Option
                  </label>
                  <input
                    type="text"
                    value={editForm.correctOptions || ""}
                    onChange={(e) => setEditForm((p) => ({ ...p, correctOptions: e.target.value }))}
                    placeholder="e.g. A"
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: "8px",
                      border: "2px solid var(--color-8, #e2e8f0)",
                      fontSize: "0.9rem",
                    }}
                  />
                </div>
              )}

              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label style={{ fontWeight: 600 }}>
                  <i className="fa-solid fa-lightbulb" style={{ marginRight: "6px", color: "var(--color-1)" }} />
                  Solution
                </label>

                <textarea
                  ref={(el) => {
                    if (el) {
                      el.style.height = "auto";
                      el.style.height = el.scrollHeight + 5 + "px";
                    }
                  }}
                  value={editForm.solution}
                  onChange={(e) => {
                    setEditForm((p) => ({ ...p, solution: e.target.value }));
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + 5 + "px";
                  }}
                  placeholder="Enter solution (optional)..."
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "2px solid var(--color-8, #e2e8f0)",
                    fontSize: "0.95rem",
                    resize: "vertical",
                    fontFamily: "inherit",
                    lineHeight: 1.6,
                    minHeight: "120px",
                    overflow: "hidden"
                  }}
                />
              </div>

              {/* Actions */}
              <div className="modal-actions" style={{ marginTop: "1.8rem" }}>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    if (!isSavingEdit) {
                      setIsEditModalOpen(false);
                      setEditQuestion(null);
                    }
                  }}
                  disabled={isSavingEdit}
                  style={{ marginRight: "10px" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSavingEdit || !isFormChanged}
                >
                  {isSavingEdit ? (
                    <><Loader fontSize="18px" style={{ left: 0, top: 0 }} /> Saving...</>
                  ) : (
                    <><i className="fa-solid fa-floppy-disk" style={{ marginRight: "6px" }} />Save Changes</>
                  )}
                </button>
              </div>
            </form>
          </Modal>

          <Modal
            isOpen={isAddQuestionBankModalOpen}
            onClose={() => {
              if (!isSubmitting) {
                setIsAddQuestionBankModalOpen(false);
              }
            }}
            maxWidth="100%"
          >
            <h2>Create New Question Bank</h2>
            <p className="modal-description" style={{ marginBottom: "2rem" }}>
              Fill in the details below to generate a new set of questions for{" "}
              {classDetails
                ? `${classDetails.name}${classDetails.level ? ` (${classDetails.level})` : ""
                }`
                : "your class"}
              .
            </p>

            <form onSubmit={handleAddQuestionBank} className="rubric-form">
              {/* Question Bank Name */}
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Question
                  Bank Name
                </label>
                <input
                  type="text"
                  value={newQuestionBank.name}
                  required
                  placeholder="Enter a descriptive name for the entire question bank"
                  onChange={(e) =>
                    setNewQuestionBank((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Table Container */}
              <div className="table-container">
                <h3
                  style={{ marginBottom: "1rem", marginTop: "1rem", color: "#333" }}
                >
                  Question Bank Entries
                </h3>

                <ModalTableInput
                  headers={[
                    {
                      key: "topic",
                      label: "Topic",
                      width: "1.8fr",
                      placeholder: "e.g. Linear Motion",
                    },
                    {
                      key: "subTopic",
                      label: "Subtopic",
                      width: "1.4fr",
                      placeholder: "e.g. Dimensions",
                    },
                    { key: "difficulty", label: "Difficulty", width: "1.5fr" },
                    { key: "questionType", label: "Question Type", width: "1.5fr" },
                    ...(newQuestionBank.entries.some((entry) =>
                      (entry.questionType || []).some(
                        (opt) => opt.value && opt.value.toUpperCase().includes("MCQ")
                      )
                    )
                      ? [
                        {
                          key: "mcqSubTypes",
                          label: "MCQ Config",
                          width: "0.7fr",
                          title: "Configure MCQ sub-types for this entry",
                        },
                      ]
                      : []),
                    {
                      key: "questionCount",
                      label: "Count",
                      width: "1fr",
                      required: true,
                    },
                    {
                      key: "actions",
                      label: "Actions",
                      width: "0.8fr",
                    },
                  ]}
                  rows={newQuestionBank.entries}
                  onRowChange={updateEntry}
                  onRowRemove={removeEntry}
                  onRowAdd={addNewEntry}
                  canRemove={true}
                  addButtonText="Add Another Entry"
                  renderCell={(header, row, rowIndex) => {
                    switch (header.key) {
                      case "topic":
                        return (
                          <input
                            type="text"
                            value={row.topic}
                            placeholder={header.placeholder}
                            onChange={(e) =>
                              updateEntry(rowIndex, "topic", e.target.value)
                            }
                            className="modal-table-input"
                          />
                        );

                      case "subTopic":
                        return (
                          <input
                            type="text"
                            value={row.subTopic}
                            placeholder={header.placeholder}
                            onChange={(e) =>
                              updateEntry(rowIndex, "subTopic", e.target.value)
                            }
                            className="modal-table-input"
                          />
                        );

                      case "difficulty":
                        return (
                          <Select
                            isMulti
                            isClearable
                            name={`difficulty-${rowIndex}`}
                            options={difficultyOptions}
                            value={row.difficulty}
                            onChange={(selectedOptions) =>
                              updateEntry(
                                rowIndex,
                                "difficulty",
                                selectedOptions || []
                              )
                            }
                            placeholder="Select difficulties..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              container: (provided) => ({
                                ...provided,
                                width: "100%",
                                fontSize: "0.875rem",
                              }),
                              control: (provided) => ({
                                ...provided,
                                minHeight: "32px",
                                border: "2px solid var(--color-8)",
                                borderRadius: "6px",
                              }),
                              multiValue: (provided) => ({
                                ...provided,
                                backgroundColor: "#fef3c7",
                                fontSize: "0.75rem",
                              }),
                              multiValueLabel: (provided) => ({
                                ...provided,
                                color: "#92400e",
                                fontSize: "0.75rem",
                              }),
                            }}
                          />
                        );

                      case "questionType":
                        return (
                          <Select
                            isMulti
                            isClearable
                            name={`questionType-${rowIndex}`}
                            options={questionTypeOptions}
                            value={row.questionType}
                            onChange={(selectedOptions) =>
                              updateEntry(
                                rowIndex,
                                "questionType",
                                selectedOptions || []
                              )
                            }
                            placeholder="Select types..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              container: (provided) => ({
                                ...provided,
                                width: "100%",
                                fontSize: "0.875rem",
                              }),
                              control: (provided) => ({
                                ...provided,
                                minHeight: "32px",
                                border: "2px solid var(--color-8)",
                                borderRadius: "6px",
                              }),
                              multiValue: (provided) => ({
                                ...provided,
                                backgroundColor: "#e0e7ff",
                                fontSize: "0.75rem",
                              }),
                              multiValueLabel: (provided) => ({
                                ...provided,
                                color: "#1e40af",
                                fontSize: "0.75rem",
                              }),
                            }}
                          />
                        );

                      case "mcqSubTypes": {
                        const rowHasMCQ = (row.questionType || []).some(
                          (opt) => opt.value && opt.value.toUpperCase().includes("MCQ")
                        );
                        if (!rowHasMCQ) {
                          return (
                            <span
                              style={{
                                color: "#94a3b8",
                                fontSize: "0.8rem",
                                textAlign: "center",
                                display: "block",
                              }}
                            >
                              —
                            </span>
                          );
                        }
                        const selectedCount = (row.mcqSubTypes || []).length;
                        return (
                          <div className="mcq-subtype-gear-wrapper">
                            <button
                              type="button"
                              className={`mcq-subtype-gear-btn ${mcqSubTypePopupRowIndex === rowIndex ? "active" : ""
                                }`}
                              title="Configure MCQ sub-types"
                              onClick={() =>
                                setMcqSubTypePopupRowIndex(
                                  mcqSubTypePopupRowIndex === rowIndex ? null : rowIndex
                                )
                              }
                            >
                              <i className="fa-solid fa-gear" />
                              {selectedCount > 0 && (
                                <span className="mcq-subtype-badge">
                                  {selectedCount}
                                </span>
                              )}
                            </button>

                            {mcqSubTypePopupRowIndex === rowIndex && (
                              <div className="mcq-subtype-popup">
                                <div className="mcq-subtype-popup-header">
                                  <span>
                                    <i
                                      className="fa-solid fa-sliders"
                                      style={{ marginRight: "0.4rem" }}
                                    />
                                    MCQ Sub-Types
                                  </span>
                                  <button
                                    type="button"
                                    className="mcq-subtype-popup-close"
                                    onClick={() => setMcqSubTypePopupRowIndex(null)}
                                  >
                                    <i className="fa-solid fa-xmark" />
                                  </button>
                                </div>
                                <div className="mcq-subtype-popup-body">
                                  {mcqSubTypeOptions.length === 0 ? (
                                    <p className="mcq-subtype-empty">
                                      No sub-types available.
                                    </p>
                                  ) : (
                                    mcqSubTypeOptions.map((option) => {
                                      const isChecked = (row.mcqSubTypes || []).some(
                                        (s) => s.value === option.value
                                      );
                                      return (
                                        <label
                                          key={option._id || option.value}
                                          className={`mcq-subtype-checkbox-label ${isChecked ? "checked" : ""
                                            }`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => {
                                              const current = row.mcqSubTypes || [];
                                              const next = isChecked
                                                ? current.filter(
                                                  (s) => s.value !== option.value
                                                )
                                                : [...current, option];
                                              updateEntry(
                                                rowIndex,
                                                "mcqSubTypes",
                                                next
                                              );
                                            }}
                                          />
                                          <span className="mcq-subtype-checkmark">
                                            {isChecked && (
                                              <i className="fa-solid fa-check" />
                                            )}
                                          </span>
                                          <span className="mcq-subtype-option-text">
                                            {option.value}
                                          </span>
                                        </label>
                                      );
                                    })
                                  )}
                                </div>
                                {mcqSubTypeOptions.length > 0 && (
                                  <div className="mcq-subtype-popup-footer">
                                    <span className="mcq-subtype-selected-count">
                                      {selectedCount} of {mcqSubTypeOptions.length} selected
                                    </span>
                                    <button
                                      type="button"
                                      className="mcq-subtype-done-btn"
                                      onClick={() => setMcqSubTypePopupRowIndex(null)}
                                    >
                                      Done
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }

                      case "questionCount":
                        return (
                          <input
                            type="number"
                            min="1"
                            max={MAX_TOTAL_QUESTIONS}
                            value={row.questionCount}
                            required
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                updateEntry(rowIndex, "questionCount", "");
                              } else {
                                const newCount = parseInt(value);
                                if (!isNaN(newCount) && newCount <= MAX_TOTAL_QUESTIONS) {
                                  updateEntry(rowIndex, "questionCount", newCount);
                                }
                              }
                            }}
                            onBlur={(e) => {
                              if (
                                e.target.value === "" ||
                                parseInt(e.target.value) < 1
                              ) {
                                updateEntry(rowIndex, "questionCount", 1);
                              } else if (parseInt(e.target.value) > MAX_TOTAL_QUESTIONS) {
                                updateEntry(rowIndex, "questionCount", MAX_TOTAL_QUESTIONS);
                              }
                            }}
                            className="modal-table-input"
                            style={{
                              borderColor:
                                calculateTotalQuestions(newQuestionBank.entries) >
                                  MAX_TOTAL_QUESTIONS
                                  ? "#dc2626"
                                  : undefined,
                            }}
                          />
                        );

                      case "actions":
                        return (
                          <button
                            type="button"
                            onClick={() => removeEntry(rowIndex)}
                            disabled={newQuestionBank.entries.length === 1}
                            className="modal-table-remove-btn"
                            title="Remove this entry"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        );

                      default:
                        return null;
                    }
                  }}
                  footerContent={
                    <div
                      className="total-count-display"
                      style={{
                        marginBottom: "1rem",
                        padding: "10px",
                        borderRadius: "6px",
                        backgroundColor:
                          calculateTotalQuestions(newQuestionBank.entries) >
                            MAX_TOTAL_QUESTIONS
                            ? "#fee2e2"
                            : calculateTotalQuestions(newQuestionBank.entries) ===
                              MAX_TOTAL_QUESTIONS
                              ? "#f0fdf4"
                              : "#f0f9ff",
                        border: `2px solid ${calculateTotalQuestions(newQuestionBank.entries) >
                          MAX_TOTAL_QUESTIONS
                          ? "#dc2626"
                          : calculateTotalQuestions(newQuestionBank.entries) ===
                            MAX_TOTAL_QUESTIONS
                            ? "#16a34a"
                            : "#3b82f6"
                          }`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "bold",
                          color:
                            calculateTotalQuestions(newQuestionBank.entries) >
                              MAX_TOTAL_QUESTIONS
                              ? "#dc2626"
                              : calculateTotalQuestions(newQuestionBank.entries) ===
                                MAX_TOTAL_QUESTIONS
                                ? "#16a34a"
                                : "#1e40af",
                        }}
                      >
                        Total Questions:{" "}
                        {calculateTotalQuestions(newQuestionBank.entries) ===
                          MAX_TOTAL_QUESTIONS
                          ? `${calculateTotalQuestions(
                            newQuestionBank.entries
                          )}/${MAX_TOTAL_QUESTIONS}`
                          : calculateTotalQuestions(newQuestionBank.entries)}
                      </span>
                      {calculateTotalQuestions(newQuestionBank.entries) >
                        MAX_TOTAL_QUESTIONS && (
                          <span style={{ color: "#dc2626", fontSize: "0.875rem" }}>
                            <i className="fa-solid fa-exclamation-triangle"></i>{" "}
                            Exceeds maximum limit of {MAX_TOTAL_QUESTIONS} Questions
                          </span>
                        )}
                      {calculateTotalQuestions(newQuestionBank.entries) ===
                        MAX_TOTAL_QUESTIONS && (
                          <span style={{ color: "#16a34a", fontSize: "0.875rem" }}>
                            <i className="fa-solid fa-check-circle"></i> Perfect!
                          </span>
                        )}
                    </div>
                  }
                />
              </div>

              {/* Source & Reference Configuration (Optional) */}
              <div
                className="source-config-container"
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "1.5rem",
                  backgroundColor: "#f8fafc",
                  marginTop: "1.5rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid #e2e8f0", paddingBottom: "0.75rem" }}>
                  <i className="fa-solid fa-book" style={{ color: "var(--color-1, #3b82f6)", fontSize: "1.1rem" }}></i>
                  <h3 style={{ color: "#1e293b", margin: 0, fontSize: "1.05rem", fontWeight: "600" }}>
                    Source & Reference Configuration
                  </h3>
                </div>

                {/* Reference Textbooks (Dynamic List) */}
                <div
                  className="form-group"
                  style={{
                    opacity: isTextbookDisabled ? 0.55 : 1,
                    pointerEvents: isTextbookDisabled ? "none" : "auto",
                    transition: "all 0.2s ease",
                  }}
                >
                  <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <span>Reference Textbooks / Names (Optional)</span>
                    {isTextbookDisabled && (
                      <span style={{ fontSize: "0.75rem", color: "#dc2626", fontWeight: "500", display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="fa-solid fa-circle-info"></i> Disabled: Only one source type allowed ({hasSourceLink ? "Link" : "PDF"} already entered)
                      </span>
                    )}
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {(newQuestionBank.source?.texts || []).map((text, index) => (
                      <div key={index} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <input
                          type="text"
                          value={text}
                          disabled={isTextbookDisabled}
                          placeholder="e.g. Campbell Biology, Introduction to Algorithms"
                          onChange={(e) => updateSourceText(index, e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          onClick={() => removeSourceText(index)}
                          className="modal-table-remove-btn"
                          disabled={isTextbookDisabled || (newQuestionBank.source.texts.length === 1 && text === "")}
                          title="Remove textbook reference"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addSourceText}
                      className="modal-table-add-btn"
                      disabled={isTextbookDisabled}
                      style={{ marginTop: "4px", alignSelf: "flex-start" }}
                    >
                      <i className="fa-solid fa-plus"></i> Add Textbook Reference
                    </button>
                  </div>
                </div>

                {/* Reference Links (Dynamic List) */}
                <div
                  className="form-group"
                  style={{
                    opacity: isLinkDisabled ? 0.55 : 1,
                    pointerEvents: isLinkDisabled ? "none" : "auto",
                    transition: "all 0.2s ease",
                  }}
                >
                  <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <span>Reference Web Links (Optional)</span>
                    {isLinkDisabled && (
                      <span style={{ fontSize: "0.75rem", color: "#dc2626", fontWeight: "500", display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="fa-solid fa-circle-info"></i> Disabled: Only one source type allowed ({hasSourceText ? "Textbook" : "PDF"} already entered)
                      </span>
                    )}
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {(newQuestionBank.source?.links || []).map((link, index) => (
                      <div key={index} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <input
                          type="text"
                          value={link}
                          disabled={isLinkDisabled}
                          placeholder="e.g. https://example.com/syllabus or online article URL"
                          onChange={(e) => updateSourceLink(index, e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          onClick={() => removeSourceLink(index)}
                          className="modal-table-remove-btn"
                          disabled={isLinkDisabled || (newQuestionBank.source.links.length === 1 && link === "")}
                          title="Remove web link"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addSourceLink}
                      className="modal-table-add-btn"
                      disabled={isLinkDisabled}
                      style={{ marginTop: "4px", alignSelf: "flex-start" }}
                    >
                      <i className="fa-solid fa-plus"></i> Add Link Reference
                    </button>
                  </div>
                </div>

                {/* Reference PDFs (PYQs) Upload */}
                <div
                  className="form-group"
                  style={{
                    opacity: isPdfDisabled ? 0.55 : 1,
                    pointerEvents: isPdfDisabled ? "none" : "auto",
                    transition: "all 0.2s ease",
                  }}
                >
                  <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <span>Reference PDF Documents (Optional)</span>
                    {isPdfDisabled && (
                      <span style={{ fontSize: "0.75rem", color: "#dc2626", fontWeight: "500", display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="fa-solid fa-circle-info"></i> Disabled: Only one source type allowed ({hasSourceText ? "Textbook" : "Link"} already entered)
                      </span>
                    )}
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div
                      style={{
                        border: "2px dashed #cbd5e1",
                        borderRadius: "8px",
                        padding: "20px",
                        textAlign: "center",
                        backgroundColor: "#ffffff",
                        cursor: isPdfDisabled ? "not-allowed" : "pointer",
                        position: "relative",
                        transition: "all 0.2s ease-in-out",
                      }}
                      onDragOver={(e) => {
                        if (isPdfDisabled) return;
                        e.preventDefault();
                        e.currentTarget.style.borderColor = "var(--color-1, #3b82f6)";
                        e.currentTarget.style.backgroundColor = "#eff6ff";
                      }}
                      onDragLeave={(e) => {
                        if (isPdfDisabled) return;
                        e.preventDefault();
                        e.currentTarget.style.borderColor = "#cbd5e1";
                        e.currentTarget.style.backgroundColor = "#ffffff";
                      }}
                      onDrop={(e) => {
                        if (isPdfDisabled) return;
                        e.preventDefault();
                        e.currentTarget.style.borderColor = "#cbd5e1";
                        e.currentTarget.style.backgroundColor = "#ffffff";
                        const droppedFiles = Array.from(e.dataTransfer.files).filter(
                          (file) => file.type === "application/pdf"
                        );
                        if (droppedFiles.length > 0) {
                          setNewQuestionBank((prev) => ({
                            ...prev,
                            source: {
                              ...prev.source,
                              files: [...(prev.source?.files || []), ...droppedFiles],
                            },
                          }));
                        }
                      }}
                    >
                      <input
                        type="file"
                        multiple
                        accept=".pdf"
                        disabled={isPdfDisabled}
                        onChange={(e) => {
                          if (isPdfDisabled) return;
                          const selectedFiles = Array.from(e.target.files || []).filter(
                            (file) => file.type === "application/pdf" || file.name.endsWith(".pdf")
                          );
                          setNewQuestionBank((prev) => ({
                            ...prev,
                            source: {
                              ...prev.source,
                              files: [...(prev.source?.files || []), ...selectedFiles],
                            },
                          }));
                        }}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          opacity: 0,
                          cursor: isPdfDisabled ? "not-allowed" : "pointer",
                        }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                        <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: "2rem", color: isPdfDisabled ? "#94a3b8" : "var(--color-1, #3b82f6)" }}></i>
                        <span style={{ fontWeight: "600", color: "#475569" }}>
                          Drag and drop PDF references here, or <span style={{ color: isPdfDisabled ? "#94a3b8" : "var(--color-1, #3b82f6)" }}>browse</span>
                        </span>
                        <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>Supported formats: PDF only</span>
                      </div>
                    </div>

                    {/* List of uploaded files */}
                    {Array.isArray(newQuestionBank.source?.files) && newQuestionBank.source.files.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {newQuestionBank.source.files.map((file, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px 12px",
                              backgroundColor: "#f1f5f9",
                              borderRadius: "6px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                              <i className="fa-solid fa-file-pdf" style={{ color: "#ef4444", fontSize: "1.1rem" }}></i>
                              <span style={{ fontSize: "0.875rem", color: "#334155", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                {file.name}
                              </span>
                              <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setNewQuestionBank((prev) => ({
                                  ...prev,
                                  source: {
                                    ...prev.source,
                                    files: prev.source.files.filter((_, i) => i !== idx),
                                  },
                                }));
                              }}
                              style={{
                                background: "none",
                                border: "none",
                                color: "#94a3b8",
                                cursor: "pointer",
                                padding: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "50%",
                                transition: "all 0.1s",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.color = "#ef4444";
                                e.currentTarget.style.backgroundColor = "#fee2e2";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.color = "#94a3b8";
                                e.currentTarget.style.backgroundColor = "transparent";
                              }}
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Execution Mode (Only active when source is provided) */}
                <div
                  className="form-group"
                  style={{
                    borderTop: "1px solid #e2e8f0",
                    paddingTop: "1.25rem",
                    opacity: hasAnySource ? 1 : 0.55,
                    pointerEvents: hasAnySource ? "auto" : "none",
                    transition: "all 0.2s ease",
                  }}
                >
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
                    <span>Question Bank Processing Mode</span>
                    {!hasAnySource && (
                      <span style={{ fontSize: "0.75rem", color: "#dc2626", fontWeight: "500", display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="fa-solid fa-circle-info"></i> Please provide a Textbook or PDF Reference first to configure mode
                      </span>
                    )}
                  </label>
                  <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                    {/* GENERATE CARD */}
                    <div
                      onClick={() => {
                        if (hasAnySource) {
                          setNewQuestionBank((prev) => ({
                            ...prev,
                            source: { ...prev.source, executionMode: "GENERATE" },
                          }));
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: "12px",
                        border: `2px solid ${hasAnySource && newQuestionBank.source?.executionMode === "GENERATE"
                          ? "var(--color-1, #3b82f6)"
                          : "#e2e8f0"
                          }`,
                        borderRadius: "8px",
                        cursor: hasAnySource ? "pointer" : "not-allowed",
                        backgroundColor:
                          hasAnySource && newQuestionBank.source?.executionMode === "GENERATE"
                            ? "#eff6ff"
                            : "#ffffff",
                        transition: "all 0.2s ease",
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                      onMouseOver={(e) => {
                        if (hasAnySource && newQuestionBank.source?.executionMode !== "GENERATE") {
                          e.currentTarget.style.borderColor = "#cbd5e1";
                        }
                      }}
                      onMouseOut={(e) => {
                        if (hasAnySource && newQuestionBank.source?.executionMode !== "GENERATE") {
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", color: hasAnySource && newQuestionBank.source?.executionMode === "GENERATE" ? "var(--color-1, #3b82f6)" : "#475569" }}>
                        <i className="fa-solid fa-wand-magic-sparkles"></i>
                        <span>Generate Similar</span>
                      </div>
                      <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                        Create brand-new questions inspired by your references and files.
                      </span>
                    </div>

                    {/* FETCH CARD */}
                    <div
                      onClick={() => {
                        if (hasAnySource) {
                          setNewQuestionBank((prev) => ({
                            ...prev,
                            source: { ...prev.source, executionMode: "FETCH" },
                          }));
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: "12px",
                        border: `2px solid ${hasAnySource && newQuestionBank.source?.executionMode === "FETCH"
                          ? "var(--color-1, #3b82f6)"
                          : "#e2e8f0"
                          }`,
                        borderRadius: "8px",
                        cursor: hasAnySource ? "pointer" : "not-allowed",
                        backgroundColor:
                          hasAnySource && newQuestionBank.source?.executionMode === "FETCH"
                            ? "#eff6ff"
                            : "#ffffff",
                        transition: "all 0.2s ease",
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                      onMouseOver={(e) => {
                        if (hasAnySource && newQuestionBank.source?.executionMode !== "FETCH") {
                          e.currentTarget.style.borderColor = "#cbd5e1";
                        }
                      }}
                      onMouseOut={(e) => {
                        if (hasAnySource && newQuestionBank.source?.executionMode !== "FETCH") {
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", color: hasAnySource && newQuestionBank.source?.executionMode === "FETCH" ? "var(--color-1, #3b82f6)" : "#475569" }}>
                        <i className="fa-solid fa-magnifying-glass"></i>
                        <span>Find & Return Exact</span>
                      </div>
                      <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                        Extract exact questions match search criteria from references.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Solutions Settings (General Option) */}
              <div className="form-group" style={{ marginTop: "1.5rem" }}>
                <label>Generate Solutions for All Questions</label>
                <div style={{ marginTop: "8px" }}>
                  <label className="solution-toggle global-toggle">
                    <input
                      type="checkbox"
                      checked={newQuestionBank.withSolution}
                      onChange={(e) =>
                        setNewQuestionBank((prev) => ({
                          ...prev,
                          withSolution: e.target.checked,
                        }))
                      }
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {newQuestionBank.withSolution ? "Yes" : "No"}
                    </span>
                  </label>
                </div>
              </div>

              {/* Custom Instructions Field - After the table */}
              <div className="form-group" style={{ marginTop: "2rem" }}>
                <label>
                  Custom Instructions{" "}
                  <span className="optional-text">(Optional)</span>
                </label>
                <textarea
                  value={newQuestionBank.customInstructions}
                  onChange={(e) =>
                    setNewQuestionBank((prev) => ({
                      ...prev,
                      customInstructions: e.target.value,
                    }))
                  }
                  rows="3"
                  placeholder="e.g. Generate questions in French, Focus on practical applications, etc."
                  className="form-textarea"
                  style={{ height: "100px" }}
                />
                <small className="helper-text">
                  Provide any special instructions for question generation that
                  apply to all entries above.
                </small>
              </div>

              {/* Submit Button */}
              {!isSubmitting ? (
                <button
                  type="submit"
                  className="btn-submit"
                  title="Generate new questions"
                  style={{
                    marginTop: "2rem",
                    opacity:
                      calculateTotalQuestions(newQuestionBank.entries) >
                        MAX_TOTAL_QUESTIONS
                        ? 0.6
                        : 1,
                    cursor:
                      calculateTotalQuestions(newQuestionBank.entries) >
                        MAX_TOTAL_QUESTIONS
                        ? "not-allowed"
                        : "pointer",
                  }}
                  disabled={
                    calculateTotalQuestions(newQuestionBank.entries) >
                    MAX_TOTAL_QUESTIONS
                  }
                >
                  <i className="fa-solid fa-wand-magic-sparkles"></i> Generate
                  Question Bank
                </button>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    marginTop: "2rem",
                    padding: "2rem",
                    borderRadius: "12px",
                    background:
                      progressPhase === "generating"
                        ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                        : progressPhase === "solutions"
                          ? "linear-gradient(135deg, #ffe8c8ff 0%, #ffe9caff 100%)"
                          : progressPhase === "completed"
                            ? "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)"
                            : "#f8f9fa",
                    border: `2px solid ${progressPhase === "generating"
                      ? "#2196f3"
                      : progressPhase === "solutions"
                        ? "#f09a21"
                        : progressPhase === "completed"
                          ? "#4caf50"
                          : "#e0e0e0"
                      }`,
                    transition: "all 0.5s ease-in-out",
                    transform: "scale(1.02)",
                    boxShadow:
                      progressPhase === "generating"
                        ? "0 8px 32px rgba(33, 150, 243, 0.3)"
                        : progressPhase === "solutions"
                          ? "0 8px 32px rgba(240, 154, 33, 0.3)"
                          : progressPhase === "completed"
                            ? "0 8px 32px rgba(76, 175, 80, 0.3)"
                            : "0 4px 16px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Loader
                      fontSize="50px"
                      style={{
                        left: "0",
                        color:
                          progressPhase === "generating"
                            ? "#1976d2"
                            : progressPhase === "solutions"
                              ? "#f09a21"
                              : progressPhase === "completed"
                                ? "#388e3c"
                                : "#666",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      color:
                        progressPhase === "generating"
                          ? "#1565c0"
                          : progressPhase === "solutions"
                            ? "#f09a21"
                            : progressPhase === "completed"
                              ? "#2e7d32"
                              : "#1e40af",
                      textAlign: "center",
                      animation: "pulse 2s infinite",
                      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    {submissionProgress}
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color:
                        progressPhase === "generating"
                          ? "#1976d2"
                          : progressPhase === "solutions"
                            ? "#f09a21"
                            : progressPhase === "completed"
                              ? "#388e3c"
                              : "#6b7280",
                      textAlign: "center",
                      fontWeight: "500",
                    }}
                  >
                    {submissionProgress.includes("Solution")
                      ? "This process may take a few minutes. Please wait..."
                      : submissionProgress.includes("Generating Questions")
                        ? "Setting up your question bank..."
                        : submissionProgress.includes("Generated Questions")
                          ? "Questions created successfully!"
                          : ""}
                  </div>
                </div>
              )}

              {calculateTotalQuestions(newQuestionBank.entries) >
                MAX_TOTAL_QUESTIONS && (
                  <div
                    style={{
                      marginTop: "1rem",
                      padding: "10px",
                      backgroundColor: "#fee2e2",
                      border: "1px solid #dc2626",
                      borderRadius: "6px",
                      color: "#dc2626",
                    }}
                  >
                    <i className="fa-solid fa-exclamation-triangle"></i> Please reduce
                    the total question count to {MAX_TOTAL_QUESTIONS} or less before
                    submitting.
                  </div>
                )}
            </form>
          </Modal>

          {/* Extract from PDF Modal */}
          <Modal
            isOpen={isExtractPdfOpen}
            onClose={() => {
              if (!isExtractingPdf) {
                setIsExtractPdfOpen(false);
                setExtractPdfForm({ questionBankName: "", sourceTestPaper: null, answerKeysFile: null, generateSolutions: false, customInstructions: "", numberOfQs: "" });
                setExtractedQuestions([]);
              }
            }}
            maxWidth={extractedQuestions.length > 0 ? "100%" : undefined}
          >
            {extractedQuestions.length === 0 ? (
              <div className="oeb-generate-modal">
                <div className="oeb-generate-modal-header">
                  <div
                    className="oeb-generate-modal-icon"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(249, 115, 22, 0.2))",
                      color: "#dc2626",
                    }}
                  >
                    <i className="fa-solid fa-file-pdf"></i>
                  </div>
                  <div>
                    <h2>Convert Test Paper to Question Bank</h2>
                    <p className="modal-description">
                      Upload a physical test paper and we&apos;ll extract questions automatically.
                    </p>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Question Bank Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter a descriptive name for the question bank"
                    value={extractPdfForm.questionBankName}
                    onChange={(e) =>
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        questionBankName: e.target.value,
                      }))
                    }
                    disabled={isExtractingPdf}
                  />
                  <small className="helper-text" style={{ color: "#666", marginTop: "0.5rem", display: "block" }}>
                    Name of the question bank where extracted questions will be added.
                  </small>
                </div>

                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Number of Questions <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    min="1"
                    placeholder="Enter number of questions you want to extract"
                    value={extractPdfForm.numberOfQs}
                    onChange={(e) =>
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        numberOfQs: e.target.value,
                      }))
                    }
                    disabled={isExtractingPdf}
                  />
                  <small className="helper-text" style={{ color: "#666", marginTop: "0.5rem", display: "block" }}>
                    Specify how many questions you want the AI to extract from the test paper.
                  </small>
                </div>

                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Test Paper <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <FileUploadSection
                    currentFileUrl={null}
                    currentFileName={
                      extractPdfForm.sourceTestPaper
                        ? extractPdfForm.sourceTestPaper.name
                        : null
                    }
                    isSubmitting={isExtractingPdf}
                    accept=".pdf"
                    onFileChange={(file) => {
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        sourceTestPaper: file,
                      }));
                    }}
                    onCapture={(callback) => {
                      setCaptureFor("create-test-paper");
                      setIsCapturing(true);
                      window.__fileUploadCallback = callback;
                    }}
                    onRemoveSelectedFile={() => {
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        sourceTestPaper: null,
                      }));
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Answer Key{" "}
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>(Optional)</span>
                    {extractPdfForm.generateSolutions && (
                      <div style={{ color: "#ef4444", marginTop: "0.5rem", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                        <i className="fa-solid fa-circle-exclamation"></i>
                        <span>Disabled because Generate Solutions is enabled.</span>
                      </div>
                    )}
                  </label>
                  <FileUploadSection
                    currentFileUrl={null}
                    currentFileName={
                      extractPdfForm.answerKeysFile
                        ? extractPdfForm.answerKeysFile.name
                        : null
                    }
                    isSubmitting={isExtractingPdf}
                    disabled={extractPdfForm.generateSolutions}
                    accept=".pdf"
                    onFileChange={(file) => {
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        answerKeysFile: file,
                      }));
                    }}
                    onCapture={(callback) => {
                      setCaptureFor("create-answer-key");
                      setIsCapturing(true);
                      window.__fileUploadCallback = callback;
                    }}
                    onRemoveSelectedFile={() => {
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        answerKeysFile: null,
                      }));
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Generate Solutions
                    {!!extractPdfForm.answerKeysFile && (
                      <div style={{ color: "#ef4444", marginTop: "0.5rem", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                        <i className="fa-solid fa-circle-exclamation"></i>
                        <span>Disabled because an Answer Key is uploaded.</span>
                      </div>
                    )}
                  </label>
                  <label className="solution-toggle">
                    <input
                      type="checkbox"
                      checked={extractPdfForm.generateSolutions}
                      onChange={(e) =>
                        setExtractPdfForm((prev) => ({
                          ...prev,
                          generateSolutions: e.target.checked,
                        }))
                      }
                      disabled={isExtractingPdf || !!extractPdfForm.answerKeysFile}
                    />
                    <span className={`toggle-track ${!!extractPdfForm.answerKeysFile ? 'disabled-track' : ''}`} style={!!extractPdfForm.answerKeysFile ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text" style={!!extractPdfForm.answerKeysFile ? { color: "#94a3b8" } : {}}>
                      {extractPdfForm.generateSolutions ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                  <small className="helper-text" style={{ color: "#666", marginTop: "0.5rem", display: "block" }}>
                    Automatically generate AI solutions for extracted questions.
                  </small>
                </div>

                <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                  <label className="form-label">
                    Custom Instructions{" "}
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>(Optional)</span>
                  </label>
                  <textarea
                    value={extractPdfForm.customInstructions}
                    onChange={(e) =>
                      setExtractPdfForm((prev) => ({
                        ...prev,
                        customInstructions: e.target.value,
                      }))
                    }
                    placeholder="e.g., Focus on conceptual questions, include diagram-based questions..."
                    disabled={isExtractingPdf}
                    style={{ width: "100%", resize: "vertical", height: "100px" }}
                  />
                  <small className="helper-text" style={{ color: "#666", marginTop: "0.5rem", display: "block" }}>
                    Provide any specific instructions for the AI when extracting questions.
                  </small>
                </div>

                {isExtractingPdf && (
                  <div className="extract-pdf-progress">
                    <div className="extract-pdf-progress-visual">
                      <div className="extract-pdf-progress-icon">
                        <i className="fa-solid fa-gears fa-spin"></i>
                      </div>
                      <div className="extract-pdf-progress-bar-container">
                        <div className="oeb-generate-progress-bar">
                          <div className="oeb-generate-progress-fill" style={{ background: "linear-gradient(90deg, #dc2626, #f97316)" }}></div>
                        </div>
                        <span>{extractPdfProgress}</span>
                      </div>
                    </div>
                    <p className="extract-pdf-progress-hint">
                      <i className="fa-solid fa-info-circle"></i>
                      Please don&apos;t close this window. AI is reading your test paper and converting questions.
                    </p>
                  </div>
                )}

                <div
                  className="modal-actions"
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    justifyContent: "flex-end",
                    marginTop: "1.5rem",
                  }}
                >
                  <button
                    type="button"
                    className="btn-submit"
                    onClick={handleExtractFromPdf}
                    disabled={
                      isExtractingPdf ||
                      !extractPdfForm.questionBankName.trim() ||
                      !extractPdfForm.sourceTestPaper ||
                      !extractPdfForm.numberOfQs
                    }
                  >
                    {isExtractingPdf ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i>{" "}
                        Extracting...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-file-import"></i> Extract Questions
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="online-exam-builder" style={{ marginTop: "0.5rem" }}>
                <div className="oeb-header" style={{ marginBottom: "1rem" }}>
                  <h3 className="oeb-title" style={{ fontSize: "1.3rem", fontWeight: 700 }}>
                    <i className="fa-solid fa-clipboard-question" style={{ marginRight: "8px", color: "var(--color-1)" }}></i>
                    Question Bank Questions
                    <span className="oeb-count" style={{ marginLeft: "8px", fontSize: "0.95rem", color: "#64748b" }}>
                      ({extractedQuestions.length} question{extractedQuestions.length !== 1 ? "s" : ""})
                    </span>
                  </h3>
                  <p style={{ color: "#64748b", fontSize: "0.88rem", marginTop: "4px" }}>
                    Review and modify the extracted questions. Correct answer changes are saved automatically.
                  </p>
                </div>

                <div style={{ paddingRight: "4px", marginBottom: "1rem" }}>
                  {extractedQuestions.map((q, idx) => (
                    <div
                      key={idx}
                      className="oe-question-card"
                      style={{
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        padding: "1rem 1.25rem",
                        marginBottom: "0.75rem",
                        transition: "all 0.2s ease",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: "1rem",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              marginBottom: "0.5rem",
                            }}
                          >
                            <span
                              style={{
                                background:
                                  "linear-gradient(135deg, var(--color-1), var(--color-2))",
                                color: "#fff",
                                borderRadius: "50%",
                                width: "28px",
                                height: "28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {idx + 1}
                            </span>
                            <div
                              style={{
                                fontSize: "0.9rem",
                                color: "#1e293b",
                                lineHeight: 1.5,
                                overflow: "hidden",
                              }}
                            >
                              <ReactMarkdown
                                children={formatLatexText(q.questionText)}
                                remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                              />
                            </div>
                          </div>

                          {(q.diagramFileURL || q.svgCode) && (
                            <div
                              className="diagram-viewer-container"
                              style={{
                                marginTop: "0.5rem",
                                marginBottom: "0.75rem",
                                marginLeft: "2.25rem",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "0.25rem",
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                                <div
                                  style={{
                                    position: "relative",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    backgroundColor: "#f8fafc",
                                    maxWidth: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                                    cursor: "pointer",
                                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                  }}
                                  onClick={() => {
                                    if (!q.svgCode && q.diagramFileURL) window.open(q.diagramFileURL, "_blank");
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "scale(1.02)";
                                    e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "scale(1)";
                                    e.currentTarget.style.boxShadow = "0 1px 3px 0 rgba(0, 0, 0, 0.05)";
                                  }}
                                  title="Click to view full size image"
                                >
                                  {q.svgCode ? (
                                    <>
                                      <style>{`
                                    .diagram-viewer-container svg {
                                      max-width: 100% !important;
                                      max-height: 220px !important;
                                      height: auto !important;
                                      width: auto !important;
                                    }
                                  `}</style>
                                      <div
                                        dangerouslySetInnerHTML={{ __html: q.svgCode }}
                                        style={{
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          maxWidth: "100%",
                                          maxHeight: "220px",
                                        }}
                                      />
                                    </>
                                  ) : (
                                    <img
                                      src={q.diagramFileURL}
                                      alt={`Diagram for Question ${idx + 1}`}
                                      style={{
                                        maxHeight: "220px",
                                        maxWidth: "100%",
                                        objectFit: "contain",
                                        display: "block",
                                      }}
                                    />
                                  )}
                                </div>
                                {(q.diagramPageFileURL || q.diagramFileURL || q.imageURL) && q.source !== "library" && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDiagramQuestion({
                                        ...q,
                                        isEditMode: true,
                                      });
                                      setIsCropModalOpen(true);
                                    }}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      padding: "8px 14px",
                                      background: "var(--color-1)",
                                      color: "#ffffff",
                                      border: "none",
                                      borderRadius: "8px",
                                      cursor: "pointer",
                                      fontWeight: "600",
                                      fontSize: "0.85rem",
                                      boxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
                                      transition: "all 0.2s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = "translateY(-1px)";
                                      e.currentTarget.style.boxShadow = "0 4px 6px rgba(79, 70, 229, 0.3)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = "translateY(0)";
                                      e.currentTarget.style.boxShadow = "0 2px 4px rgba(79, 70, 229, 0.2)";
                                    }}
                                  >
                                    <i className="fa-solid fa-crop-simple"></i> Edit Diagram
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          {q.options && q.options.filter(Boolean).length > 0 && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.6rem",
                                marginLeft: "2.25rem",
                                marginBottom: "1rem",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "0.4rem 1rem",
                                }}
                              >
                                {q.options.filter(Boolean).map((opt, oi) => {
                                  const optLabel = (opt && typeof opt === 'object') ? opt.key : (opt ? opt.charAt(0) : '');
                                  const optText = (opt && typeof opt === 'object') ? `${opt.key}) ${opt.text}` : (opt || '');
                                  const isCorrect =
                                    q.correctOptions &&
                                    q.correctOptions.includes(optLabel);
                                  return (
                                    <div
                                      key={oi}
                                      style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "0.4rem",
                                        fontSize: "0.85rem",
                                        color: isCorrect ? "#16a34a" : "#475569",
                                        fontWeight: isCorrect ? 600 : 400,
                                        background: isCorrect
                                          ? "#f0fdf4"
                                          : "transparent",
                                        padding: "4px 8px",
                                        borderRadius: "6px",
                                        border: isCorrect
                                          ? "1px solid #bbf7d0"
                                          : "1px solid transparent",
                                        transition: "all 0.2s ease",
                                      }}
                                    >
                                      <span
                                        style={{
                                          lineHeight: 1.4,
                                          width: "100%",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <ReactMarkdown
                                          children={formatLatexText(optText)}
                                          remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
                                          rehypePlugins={[[rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }]]}
                                          components={{ p: "span" }}
                                        />
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>

                              <div style={{ marginTop: "0.5rem" }}>
                                <label
                                  style={{
                                    fontSize: "0.78rem",
                                    color: "#64748b",
                                    fontWeight: 500,
                                    marginBottom: "0.4rem",
                                    display: "block",
                                  }}
                                >
                                  Correct Answer:
                                  {(!q.correctOptions ||
                                    q.correctOptions.length === 0) && (
                                      <span
                                        style={{
                                          color: "#ef4444",
                                          fontStyle: "italic",
                                          marginLeft: "0.5rem",
                                        }}
                                      >
                                        *Required
                                      </span>
                                    )}
                                </label>
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                  {["A", "B", "C", "D"].map((optLabel) => {
                                    const isCorrect =
                                      q.correctOptions &&
                                      q.correctOptions.includes(optLabel);
                                    return (
                                      <button
                                        key={optLabel}
                                        type="button"
                                        onClick={() =>
                                          handleReviewQuestionChange(
                                            idx,
                                            "correctOptions",
                                            [optLabel]
                                          )
                                        }
                                        style={{
                                          padding: "6px 14px",
                                          borderRadius: "20px",
                                          border: isCorrect
                                            ? "1px solid #16a34a"
                                            : "1px solid #cbd5e1",
                                          background: isCorrect ? "#16a34a" : "#fff",
                                          color: isCorrect ? "#fff" : "#475569",
                                          fontSize: "0.85rem",
                                          fontWeight: 600,
                                          cursor: "pointer",
                                          transition: "all 0.2s",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.3rem",
                                        }}
                                      >
                                        {optLabel}
                                        {isCorrect && (
                                          <i
                                            className="fa-solid fa-check"
                                            style={{ fontSize: "0.7rem" }}
                                          />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleReviewQuestionRemove(idx)}
                          className="modal-table-remove-btn"
                          style={{ flexShrink: 0, width: "32px", height: "32px" }}
                        >
                          <i
                            className="fa-solid fa-trash"
                            style={{ fontSize: "0.75rem" }}
                          ></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  className="modal-actions"
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    justifyContent: "flex-end",
                    marginTop: "1.5rem",
                    borderTop: "1px solid #e2e8f0",
                    paddingTop: "1rem",
                  }}
                >
                  <button
                    type="button"
                    className="btn-submit"
                    onClick={handleFinishReview}
                  >
                    <i className="fa-solid fa-check"></i> Finish & Save
                  </button>
                </div>
              </div>
            )}
          </Modal>
        </>
      )}
    </>
  );
};

export default ManageQuestions;
